/**
 * queries.ts — 数据查询层（TanStack Query；接真 + adapter 塑形 / mock 缺口域）
 *
 * 页面唯一取数入口：所有页面只从 '~/data/queries' 取数据与类型，禁止直接 import '~/data/mock'。
 * 每个 hook 用 useQuery 暴露 { data, isLoading }，签名 / 返回形状对页面 100% 冻结。
 *
 * 接真（// @real）：有真实端点 + 数据的域 —— queryFn 调 ~/hooks/use-*.ts 的真实 API 函数
 * 或直接走 ~/api/client 的 apiRequest（均自动带 JWT、解包 envelope 返 data），按需 N+1
 * （关联名 / 聚合 stats），再用 ~/data/adapters/* 塑成页面 row。
 * 真 API 普遍缺 mock 的聚合 / 关联名字段：能 N+1 的走 N+1，缺的落合理默认（adapter 内注释标注）。
 *
 * 全域已接真：原 6 缺口域（users / risk / finance / refund-alerts / dashboard / sys-config）+ refunds
 * 后端就绪后已切真，queryFn 走 apiRequest + 对应 adapter，签名 / 返回形状对页面 100% 冻结。
 *
 * 返回统一为 { data, isLoading }：loading 阶段 data 给空数组 / 合理默认对象，避免页面解构 undefined 崩溃。
 */

export { useRewardedAdMetrics } from '~/hooks/use-rewarded-ad-metrics';

import type {
  AdPlanDetail,
  AdPlanListItem,
  AdPlanStatus,
  AdaptyCredentialResponse,
  AdaptyEventReviewAdmissionKind,
  AdaptyEventReviewErrorCode,
  AdaptyEventReviewQueueRow,
  AdaptyEventReviewResolutionStatus,
  AdminAccountResponse,
  AdminAuditLogResponse,
  AdminUserDetailResponse,
  AppPaymentMethodResponse,
  AppPlatform,
  AppResponse,
  AuditLogFacetsResponse,
  BannerSlideResponse,
  BookAppStatusResponse,
  BookListQuery,
  BookOperationLogItem,
  BookOperationLogQuery,
  ConfigLanguageTabModule,
  ConfigLanguageTabsResponse,
  CreateAdPlanRequest,
  DashboardOverviewResponse,
  DashboardRange,
  DeeplCredentialResponse,
  FeedbackDetailResponse,
  FeedbackListItem,
  FinanceDimensionStatsResponse,
  FinanceProductSalesResponse,
  FinanceRefundTrendPoint,
  HomeSectionNovelResponse,
  HomeSectionStrategyResponse,
  LandingPageDto,
  LandingProductDto,
  ListPushTasksQuery,
  MetricsRange,
  NotificationPrefsResponse,
  NovelGroupSummary,
  NovelResponse,
  NovelVisibility,
  OpsConfigSource,
  PaymentMethodOptionsResponse,
  PaymentMethodPolicyResponse,
  PaymentMethodSettingResponse,
  PaywallConfigResponse,
  PendingInviteResponse,
  RefundRecordResponse,
  RiskRuleResponse,
  RiskRuleV1Response,
  SelfSessionResponse,
  StripeCheckoutConfigResponse,
  SystemEventLogItem,
  SystemLanguageResponse,
  SystemRegionResponse,
  TransactionImportAttributionStatus,
  TransactionImportQueueRow,
  TrustedDeviceResponse,
  UpdateAdPlanRequest,
  UserChapterReadingRecordItem,
  UserChapterUnlockRecordItem,
  UserConsumeRecordItem,
  UserListQuery,
  UserNovelReadingRecordItem,
  UserTagDto,
  UsersStatsResponse,
} from '@novel-admin/shared';
import { SETTLEMENT_CHANNELS, SYSTEM_ERROR_CODES } from '@novel-admin/shared';
import type {
  BailianCredentialResponse,
  HuggingFaceCredentialResponse,
  OllamaCredentialResponse,
} from '@novel-admin/shared';
import type { OpenRouterCredentialResponse } from '@novel-admin/shared';
import type { PaywallConversionData, PaywallConversionDimension } from '@novel-admin/shared';
import type { ActivationConversionData, ActivationConversionDimension } from '@novel-admin/shared';
import type { WebFunnelAnalysis, WebFunnelData, WebFunnelDimension } from '@novel-admin/shared';
import type { FirstPaymentCohortData, FirstPaymentCohortDimension } from '@novel-admin/shared';
import type {
  RetentionCohortData,
  RetentionCohortGranularity,
  RetentionCohortUserType,
} from '@novel-admin/shared';
import type { UseMutationResult } from '@tanstack/react-query';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { type OffsetPagedResult, apiRequest } from '~/api/client';
import { AdminApiError } from '~/api/errors';
import { useAppScope } from '~/app/app-scope-store';
import { useFinanceTimeStore } from '~/app/finance-time-store';
import { type AdPlanListFilters, adPlansApi } from '~/hooks/use-ad-plans';
import { adaptyApi } from '~/hooks/use-adapty';
import { adminUsersApi, auditLogsApi } from '~/hooks/use-admin-users';
import {
  getApp,
  listAppPaymentMethods,
  listApps,
  readingPassLanguageConfigsApi,
} from '~/hooks/use-apps';
import { listPendingInvites } from '~/hooks/use-auth';
import { bailianApi } from '~/hooks/use-bailian';
import {
  type AppOverrideRow,
  booksApi,
  categoriesApi,
  chaptersApi,
  tagsApi,
} from '~/hooks/use-books';
import { deeplApi } from '~/hooks/use-deepl';
import { feedbackApi } from '~/hooks/use-feedback';
import { financeApi } from '~/hooks/use-finance';
import { huggingFaceApi } from '~/hooks/use-huggingface';
import { landingAiConfigApi } from '~/hooks/use-landing-ai-config';
import { landingDesensitizationLogsApi } from '~/hooks/use-landing-ai-desensitization';
import { landingAiProviderConcurrencyApi } from '~/hooks/use-landing-ai-provider-concurrency';
import { landingAiReviewsApi } from '~/hooks/use-landing-ai-reviews';
import { landingPagesApi } from '~/hooks/use-landing-pages';
import { landingProductsApi } from '~/hooks/use-landing-products';
import {
  type LandingTemplatesPayload,
  landingMediaSourcesApi,
  landingTemplatesApi,
} from '~/hooks/use-landing-templates';
import { meApi } from '~/hooks/use-me';
import { ollamaApi } from '~/hooks/use-ollama';
import { openrouterApi } from '~/hooks/use-openrouter';
import { opsApi } from '~/hooks/use-ops';
import { paymentMethodOptionsApi } from '~/hooks/use-payment-method-options';
import { paymentMethodPoliciesApi } from '~/hooks/use-payment-method-policies';
import { paymentMethodSettingsApi } from '~/hooks/use-payment-method-settings';
import { paymentOffersApi } from '~/hooks/use-payment-offers';
import { paymentPagesApi } from '~/hooks/use-payments';
import { productMetadataApi } from '~/hooks/use-product-metadata';
import { prohibitedTermsApi } from '~/hooks/use-prohibited-terms';
import { pushApi } from '~/hooks/use-push';
import { rewardedAdsApi } from '~/hooks/use-rewarded-ads';

import { pushAnalyticsApi } from '~/hooks/use-push-analytics';
import { stripeCheckoutApi } from '~/hooks/use-stripe-checkout';
import { userTagsApi } from '~/hooks/use-user-tags';
import { usersApi } from '~/hooks/use-users';
import { webPaymentProductsApi } from '~/hooks/use-web-payment-products';
import { currencyMinorToMajorNumber, fmtCurrencyMinor } from '~/lib/format';
import { registerLangNames } from '~/lib/format';
import { visibilityDisplayOf } from '~/lib/novel-visibility-display';
import { useAuthStore } from '~/stores/auth-store';
import { adaptAppToRow } from './adapters/apps-adapter';
import {
  adaptChapter,
  adaptNovelToBookRow,
  selectSummaryDisplayLanguage,
} from './adapters/books-adapter';
import { type CatTagRow, adaptCategory, adaptTag } from './adapters/cat-tag-adapter';
import { type DashboardViewData, adaptDashboardOverview } from './adapters/dashboard-adapter';
import { adaptFeedbackListItem } from './adapters/feedback-adapter';
import { adaptFinanceDimensionRows, adaptFinanceProductSales } from './adapters/finance-adapter';
import { type LandingProductRow, adaptLandingProduct } from './adapters/landing-products-adapter';
import {
  type ActivationConversionViewData,
  adaptMetricsActivationConversion,
} from './adapters/metrics-activation-conversion-adapter';
import {
  type MetricsDiagnosisResponse,
  type MetricsDiagnosisViewData,
  adaptMetricsDiagnosis,
} from './adapters/metrics-diagnosis-adapter';
import {
  type MetricsEngagementResponse,
  type MetricsEngagementViewData,
  adaptMetricsEngagement,
} from './adapters/metrics-engagement-adapter';
import {
  type FirstPaymentCohortViewData,
  adaptMetricsFirstPaymentCohorts,
} from './adapters/metrics-first-payment-cohorts-adapter';
import {
  type LtvCohortGroupBy,
  type MetricsLtvCohortsResponse,
  type MetricsLtvCohortsViewData,
  adaptMetricsLtvCohorts,
} from './adapters/metrics-ltv-adapter';
import {
  type MetricsMonetizationResponse,
  type MetricsMonetizationViewData,
  adaptMetricsMonetization,
} from './adapters/metrics-monetization-adapter';
import {
  type PaywallConversionViewData,
  adaptMetricsPaywallConversion,
  withZeroPaymentChannelRows,
} from './adapters/metrics-paywall-conversion-adapter';
import {
  type RetentionCohortViewData,
  adaptMetricsRetentionCohorts,
} from './adapters/metrics-retention-cohorts-adapter';
import {
  type WebFunnelViewData,
  adaptMetricsWebFunnels,
} from './adapters/metrics-web-funnels-adapter';
import {
  type FaqCategoryRow,
  fromBanner,
  fromFaq,
  fromFaqCategory,
  fromHomeSection,
} from './adapters/ops-adapter';
import { type OfferRow, adaptOffer } from './adapters/payment-offer-adapter';
import { type PaywallPageItem, adaptPaymentPages } from './adapters/paywall-adapter';
import { type ProductMetadataRow, adaptProductMetadata } from './adapters/product-metadata-adapter';
import { adaptPushTasks, adaptPushTemplates } from './adapters/push-adapter';
import { type RiskRuleRow, adaptRiskRules } from './adapters/risk-adapter';
import { adaptSystemLanguages, adaptSystemRegions } from './adapters/sysconfig-adapter';
import { adaptUserListItem } from './adapters/users-adapter';

// 风控规则页面行（mock RiskRule + 条件三列）：页面 / 弹窗统一从 '~/data/queries' 取该类型
export type { RiskRuleRow } from './adapters/risk-adapter';
// 分类 / 标签真实行（含 id，编辑写动作用）：页面统一从 '~/data/queries' 取该类型
export type { CatTagRow } from './adapters/cat-tag-adapter';
// FAQ 分类展示行（faq_categories 注册表塑形）：FAQEditor 下拉 / OpsPage 分组表头统一从 '~/data/queries' 取该类型
export type { FaqCategoryRow } from './adapters/ops-adapter';

// 支付页面行（PaymentPageResponse 塑形）：PaywallPage 统一从 '~/data/queries' 取该类型
export type { PaywallPageItem } from './adapters/paywall-adapter';
// product_metadata 业务效果行（含 id / titleI18n，编辑写动作用）：PaywallPage 统一从 '~/data/queries' 取该类型
export type { OfferRow } from './adapters/payment-offer-adapter';
export type { ProductMetadataRow } from './adapters/product-metadata-adapter';

// ---------- 行类型 re-export（页面统一从 '~/data/queries' 取类型）----------
export type {
  AppRow,
  Series,
  BreakdownRow,
  RetentionRow,
  FunnelRow,
  RealtimeRow,
  BookRow,
  ChapterRow,
  UserRow,
  RefundRow,
  FeedbackRow,
  PushTemplate,
  PushHistoryRow,
  Banner,
  HomeBlock,
  Faq,
  FinanceRow,
  ProductSales,
  RefundAlertRule,
  RefundAlertEvent,
  RiskRule,
  DisplayNameI18n,
  SystemLanguage,
  SystemRegion,
  ExpandedLanguage,
  ExpandedRegion,
} from './mock';

// ---------- apps 域契约 DTO 类型 re-export（详情表单按契约字段受控初始化用）----------
export type {
  AppPaymentMethodResponse,
  AppResponse,
  TaskCenterRewards,
  ReadingPassLanguageConfigResponse,
} from '@novel-admin/shared';

import type {
  AppRow,
  Banner,
  BookRow,
  ChapterRow,
  Faq,
  FeedbackRow,
  FinanceRow,
  HomeBlock,
  ProductSales,
  PushHistoryRow,
  PushTemplate,
  RefundRow,
  SystemLanguage,
  SystemRegion,
  UserRow,
} from './mock';

// ---------- Apps ----------
export { getApp };
const APPS_QUERY_KEY = ['apps'] as const;

export const appQueryKeys = {
  all: APPS_QUERY_KEY,
  list: (platform?: AppPlatform) =>
    platform === undefined ? APPS_QUERY_KEY : ([...APPS_QUERY_KEY, 'platform', platform] as const),
  detail: (appId: string) => [...APPS_QUERY_KEY, appId] as const,
  paymentMethods: (appId: string) => ['app-payment-methods', appId] as const,
  readingPassLanguages: (appId: string) => ['reading-pass-language-configs', appId] as const,
};

export function useApps(platform?: AppPlatform): {
  data: AppRow[];
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const q = useQuery({
    queryKey: appQueryKeys.list(platform),
    queryFn: async (): Promise<AppRow[]> => {
      // @real — listApps()（GET /api/v1/apps）→ adaptAppToRow
      // books 直接映射 required AppResponse.book_count，0 也是真实可见书组数；
      // users / dau / revenue / refundRate 仍属其他域的可选聚合，未注入时由 adapter 保持 undefined。
      const apps = await listApps(platform);
      return apps.map((app) => adaptAppToRow(app));
    },
  });
  return {
    data: q.data ?? [],
    isLoading: q.isLoading,
    isSuccess: q.isSuccess,
    // rollout 旧 DTO 的 platform 无法安全归一时必须显式暴露失败，不能让 [] fallback 冒充真空列表。
    isError: q.isError,
    error: q.error,
    refetch: () => {
      void q.refetch();
    },
  };
}

/** App 详情原始契约 DTO（详情 5 tab 受控表单按契约字段初始化；queryKey 前缀 ['apps'] 与列表同 invalidate） */
export function useAppDetail(appId: string, enabled = true) {
  const q = useQuery({
    queryKey: appQueryKeys.detail(appId),
    enabled,
    queryFn: async (): Promise<AppResponse> => {
      // @real — getApp(id)（GET /api/v1/apps/:id）→ 原样 AppResponse（不经 adapter，表单字段逐项映射契约）
      return getApp(appId);
    },
  });
  return {
    data: q.data ?? null,
    isLoading: q.isLoading,
    isSuccess: q.isSuccess,
    isError: q.isError,
    error: q.error,
    refetch: q.refetch,
  };
}

/** 阅读卡语言配置 GET 为完整数组，不套分页、不丢弃已移出允许语言的历史配置。 */
export function useReadingPassLanguageConfigs(appId: string, enabled = true) {
  return useQuery({
    queryKey: appQueryKeys.readingPassLanguages(appId),
    enabled,
    // @real — GET /apps/:id/reading-pass-language-configs，服务端按 language 升序。
    queryFn: () => readingPassLanguageConfigsApi.list(appId),
  });
}

/**
 * App 支付渠道行（app_payment_methods，渠道闭集 IAP/STRIPE × scenario）。
 *
 * 同时暴露 `isError` 与 `isSuccess`：消费方若靠 `!isLoading && !isError` 判「拿到数据了」，会在
 * TanStack 断网态（`fetchStatus='paused'` + `status='pending'` → 两个 flag 同时为 false）把空集
 * 当成真实响应。需要区分「成功拿到空集」与「什么都没拿到」的消费方（如通道下发阻断项面板）必须读
 * `isSuccess`。
 */
export function useAppPaymentMethods(appId: string): {
  data: AppPaymentMethodResponse[];
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
} {
  const q = useQuery({
    queryKey: appQueryKeys.paymentMethods(appId),
    queryFn: async (): Promise<AppPaymentMethodResponse[]> => {
      // @real — listAppPaymentMethods（GET /api/v1/apps/:id/payment-methods）
      return listAppPaymentMethods(appId);
    },
  });
  return {
    data: q.data ?? [],
    isLoading: q.isLoading,
    isError: q.isError,
    isSuccess: q.isSuccess,
  };
}

export type { LandingProductRow };

const LANDING_PRODUCTS_FALLBACK: LandingProductRow[] = [];

/** 投放产品详情：补齐分页列表外的已选产品，保持任务编辑可用。 */
export function useLandingProduct(productId: string | null) {
  return useQuery({
    queryKey: ['landing-products', 'detail', productId],
    enabled: !!productId,
    // @real — GET /api/v1/landing-products/:id
    queryFn: () => {
      if (!productId) throw new Error('未选择投放产品');
      return landingProductsApi.get(productId);
    },
  });
}

/** 投放产品列表（ADM-LP-S001）：offset 分页，全局模块不按 App 切分 */
export function useLandingProductsPage(params: { page: number; pageSize: number }): {
  data: LandingProductRow[];
  total: number;
  isLoading: boolean;
  isError: boolean;
} {
  const q = useQuery({
    queryKey: ['landing-products', 'page', params.page, params.pageSize],
    queryFn: async (): Promise<{ rows: LandingProductRow[]; total: number }> => {
      // @real — GET /api/v1/landing-products?page&limit；App 名是展示 enrichment，失败不挡列表
      const res = await landingProductsApi.list(params.page, params.pageSize);
      let apps: Awaited<ReturnType<typeof listApps>> = [];
      let namesReady = false;
      try {
        apps = await listApps();
        namesReady = true;
      } catch {
        apps = [];
      }
      const appNameById: Record<string, string> = {};
      for (const a of apps) appNameById[a.id] = a.name;
      return {
        total: res.total,
        rows: res.data.map((dto: LandingProductDto) =>
          adaptLandingProduct(dto, appNameById, namesReady),
        ),
      };
    },
  });
  return {
    data: q.data?.rows ?? LANDING_PRODUCTS_FALLBACK,
    total: q.data?.total ?? 0,
    isLoading: q.isLoading,
    isError: q.isError,
  };
}

/** 落地页任务列表（ADM-LP-S004）：offset 分页，全局模块 */
export function useLandingPagesPage(params: { page: number; pageSize: number }): {
  data: LandingPageDto[];
  total: number;
  isLoading: boolean;
  isError: boolean;
} {
  const q = useQuery({
    queryKey: ['landing-pages', 'page', params.page, params.pageSize],
    queryFn: () => {
      // @real — GET /api/v1/landing-pages?page&limit
      return landingPagesApi.list(params.page, params.pageSize);
    },
  });
  return {
    data: q.data?.data ?? [],
    total: q.data?.total ?? 0,
    isLoading: q.isLoading,
    isError: q.isError,
  };
}

const LANDING_TASK_NOVELS_FALLBACK: Record<string, NovelResponse> = {};

/** 任务列表书行：GET /books/:id/language，用 slug+title 当可阅读编码（C 端无 book_code 列）。 */
export function useLandingTaskNovels(novelIds: string[]): {
  byId: Record<string, NovelResponse>;
  isLoading: boolean;
} {
  const key = [...new Set(novelIds.filter(Boolean))].sort();
  const q = useQuery({
    queryKey: ['books', 'landing-task-novels', key],
    enabled: key.length > 0,
    queryFn: async (): Promise<Record<string, NovelResponse>> => {
      const settled = await Promise.allSettled(key.map((id) => booksApi.language(id)));
      const out: Record<string, NovelResponse> = {};
      settled.forEach((r, i) => {
        const id = key[i];
        if (r.status === 'fulfilled' && id) out[id] = r.value;
      });
      return out;
    },
  });
  return {
    byId: q.data ?? LANDING_TASK_NOVELS_FALLBACK,
    isLoading: q.isLoading,
  };
}

/** 落地页模板清单（ADM-LP-S003）。生成器未接入 → isError，不编造模板。 */
export function useLandingTemplates(): {
  data: LandingTemplatesPayload | null;
  isLoading: boolean;
  isError: boolean;
} {
  const q = useQuery({
    queryKey: ['landing-templates'],
    queryFn: () => landingTemplatesApi.get(),
    retry: false,
    refetchInterval: (query) =>
      query.state.data?.copy_provisioning?.some((item) => item.status !== 'ready') ? 30_000 : false,
  });
  return { data: q.data ?? null, isLoading: q.isLoading, isError: q.isError };
}

export function useRetryLandingLocaleCopy() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (language: string) => landingTemplatesApi.retryCopy(language),
    onSuccess: () => client.invalidateQueries({ queryKey: ['landing-templates'] }),
  });
}

/** 投放渠道白名单（ADM-LP-S002） */
export function useLandingMediaSources(): {
  data: string[];
  isLoading: boolean;
  isError: boolean;
} {
  const q = useQuery({
    queryKey: ['landing-media-sources'],
    queryFn: async () => {
      const res = await landingMediaSourcesApi.list();
      return res.media_sources;
    },
  });
  return { data: q.data ?? [], isLoading: q.isLoading, isError: q.isError };
}

/**
 * 计划可关联的落地页候选（AF §4）：**该书下同渠道**的任务，与后端整笔校验同判据。
 *
 * 过滤放在服务端（`novel_id` + `media_source` 两个 query）：拉一大页再客户端筛的话，
 * 落地页总数超过 limit 上限后候选会**静默少几条**，运营看不出漏了谁。
 *
 * **翻页而不是一次取满**：一次取满迟早撞 limit 硬上限（200），撞上之后超出的候选既勾不上、
 * 已关联的也取消不了 —— 那是把分页问题伪装成一行提示，操作闭环走不完。
 */
export function useAdPlanLandingPageCandidates(params: {
  novelId: string;
  mediaSource: string;
  page: number;
  enabled: boolean;
}): {
  data: LandingPageDto[];
  total: number;
  pageSize: number;
  isLoading: boolean;
  isError: boolean;
} {
  const q = useQuery({
    queryKey: ['landing-pages', 'candidates', params.novelId, params.mediaSource, params.page],
    enabled: params.enabled && !!params.novelId && !!params.mediaSource,
    queryFn: () => {
      // @real — GET /api/v1/landing-pages?page&limit&novel_id&media_source
      return landingPagesApi.list(params.page, CANDIDATE_PAGE_SIZE, {
        novel_id: params.novelId,
        media_source: params.mediaSource,
      });
    },
  });
  return {
    data: q.data?.data ?? [],
    total: q.data?.total ?? 0,
    pageSize: CANDIDATE_PAGE_SIZE,
    isLoading: q.isLoading,
    isError: q.isError,
  };
}

/** 候选每页条数：翻页存在，故不必贴着 limit 硬上限 200 取；小一点渲染也快 */
const CANDIDATE_PAGE_SIZE = 50;

// ---------- 投放计划（ADM-AP-S001 / S002 / S003）----------

/**
 * 计划列表（AF §2）：offset 分页 + 三项服务端过滤。
 *
 * 过滤值进 queryKey：不进的话切换筛选命中同一份缓存，页面显示的是上一组条件的结果。
 */
export function useAdPlansPage(params: {
  page: number;
  pageSize: number;
  filters: AdPlanListFilters;
}): {
  data: AdPlanListItem[];
  total: number;
  isLoading: boolean;
  isError: boolean;
} {
  const { novel_id, media_source, status } = params.filters;
  const q = useQuery({
    queryKey: ['ad-plans', 'page', params.page, params.pageSize, novel_id, media_source, status],
    queryFn: () => {
      // @real — GET /api/v1/ad-plans?page&limit&novel_id&media_source&status
      return adPlansApi.list(params.page, params.pageSize, params.filters);
    },
  });
  return {
    data: q.data?.data ?? [],
    total: q.data?.total ?? 0,
    isLoading: q.isLoading,
    isError: q.isError,
  };
}

/** 计划详情（AF §2）：含 `landing_pages[]` 关联摘要。`id` 为空时不发请求。 */
export function useAdPlanDetail(id: string | null): {
  data: AdPlanDetail | null;
  isLoading: boolean;
  isError: boolean;
} {
  const q = useQuery({
    queryKey: ['ad-plans', 'detail', id],
    enabled: !!id,
    queryFn: () => {
      // @real — GET /api/v1/ad-plans/:id
      return adPlansApi.get(id as string);
    },
  });
  return { data: q.data ?? null, isLoading: q.isLoading, isError: q.isError };
}

/**
 * 计划的五个写动作（AF §1/§2/§3/§4）。
 *
 * 集中在一处而不是各弹窗自己 `useMutation`：五个动作**失效的是同一批缓存**（列表 + 详情），
 * 散着写迟早有一处漏掉 `invalidateQueries`，表现为「保存成功但列表没变」。
 */
export function useAdPlanMutations(): {
  create: UseMutationResult<AdPlanDetail, Error, CreateAdPlanRequest>;
  update: UseMutationResult<AdPlanDetail, Error, { id: string; body: UpdateAdPlanRequest }>;
  updateStatus: UseMutationResult<AdPlanDetail, Error, { id: string; status: AdPlanStatus }>;
  replaceLandingPages: UseMutationResult<AdPlanDetail, Error, { id: string; ids: string[] }>;
  remove: UseMutationResult<{ ok: boolean }, Error, string>;
} {
  const qc = useQueryClient();
  // 前缀失效：不带 page / pageSize / 过滤值，否则只有「当前这一组条件」的缓存被刷新
  const invalidate = async () => {
    await qc.invalidateQueries({ queryKey: ['ad-plans'] });
  };

  return {
    create: useMutation({
      mutationFn: (body: CreateAdPlanRequest) => adPlansApi.create(body),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: UpdateAdPlanRequest }) =>
        adPlansApi.update(id, body),
      onSuccess: invalidate,
    }),
    updateStatus: useMutation({
      mutationFn: ({ id, status }: { id: string; status: AdPlanStatus }) =>
        adPlansApi.updateStatus(id, status),
      onSuccess: invalidate,
    }),
    replaceLandingPages: useMutation({
      mutationFn: ({ id, ids }: { id: string; ids: string[] }) =>
        adPlansApi.replaceLandingPages(id, { landing_page_ids: ids }),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => adPlansApi.remove(id),
      onSuccess: invalidate,
    }),
  };
}

// ---------- Books ----------
export type CursorPageView<T> = {
  data: T[];
  isLoading: boolean;
  nextCursor: string | null;
  hasMore: boolean;
};

/** offset 分页视图（books 列表）：真实 total，供分页器显示真实总数 / 总页数 + 跳任意页 */
export type OffsetPageView<T> = {
  data: T[];
  isLoading: boolean;
  isSuccess?: boolean;
  isError?: boolean;
  total: number;
  hasMore: boolean;
};

/**
 * 把书籍筛选对象塑成稳定的 queryKey 片段：标量原样、数组排序后保证 key 顺序无关。
 * filter 变化 → key 变化 → TanStack 重取（不服务陈旧页）；同一 filter（仅数组顺序不同）→ 同 key 命中缓存。
 */
function bookFilterKey(filters?: BookListQuery): string {
  if (!filters) return '';
  return JSON.stringify({
    q: filters.q ?? '',
    visibility: filters.visibility ?? '',
    is_recommended: filters.is_recommended ?? null,
    source: [...(filters.source ?? [])].sort(),
    category: [...(filters.category ?? [])].sort(),
    tag: [...(filters.tag ?? [])].sort(),
    lang: [...(filters.lang ?? [])].sort(),
    serial_status: [...(filters.serial_status ?? [])].sort(),
  });
}

/**
 * 把推送任务筛选对象塑成稳定的 queryKey 片段（同 bookFilterKey 口径）。
 * 注意：后端把 cursor scope 绑定到 filter 集——cursor 在另一 filter 集下会被拒（422）；
 * 故页面改 filter 时必须把 cursor 重置回第一页，filterKey 进 queryKey 仅保证 filter 变化触发重取。
 */
function pushFilterKey(filters?: ListPushTasksQuery): string {
  if (!filters) return '';
  return JSON.stringify({
    status: [...(filters.status ?? [])].sort(),
    target_scope: filters.target_scope ?? '',
    app_id: [...(filters.app_id ?? [])].sort(),
    task_type: [...(filters.task_type ?? [])].sort(),
    operator: [...(filters.operator ?? [])].sort(),
  });
}

export function useBooksPage(params: {
  page: number;
  limit: number;
  /**
   * 服务端下推筛选（ADM-BOK-S006，P1 回归修复）：q/status 单值；source/category/tag/lang/serial_status 多值。
   * offset 分页（admin 免 C 端 cursor 契约，dev-docs 40 §1.1.1）：filterKey 进 queryKey 保证 filter 变化时
   * 整条 query 重取；页面须在 filter 变化时把 page 重置回第一页（offset 无 scope 绑定，但仍需回首页避免越界空页）。
   */
  filters?: BookListQuery;
}): OffsetPageView<BookRow> & { isSuccess: boolean; isError: boolean } {
  const { categoryNames, tagNames } = useBookTaxonomyNames();
  const q = useQuery({
    queryKey: ['books', 'page', params.page, params.limit, bookFilterKey(params.filters)],
    // @real — 书籍页独立请求，分类、标签的延迟或失败不阻塞书籍展示。
    queryFn: () => booksApi.listOffsetPaged(params.page, params.limit, params.filters),
  });
  const data = useMemo(
    () =>
      (q.data?.data ?? []).map((group) => {
        const member = selectSummaryDisplayLanguage(group, params.filters);
        return adaptNovelToBookRow(group, {
          displayLanguage: member,
          categoryName: member ? (categoryNames[member.category_id] ?? '') : '',
          tagNames: (member?.tags ?? []).map((id) => tagNames[id] ?? '').filter(Boolean),
        });
      }),
    [q.data, params.filters, categoryNames, tagNames],
  );
  return {
    data,
    isLoading: q.isLoading,
    isSuccess: q.isSuccess,
    isError: q.isError,
    total: q.data?.total ?? 0,
    hasMore: q.data?.hasMore ?? false,
  };
}

/** 与筛选器、分类管理共用缓存；名称到达后补齐行数据，不影响书籍分页查询状态。 */
function useBookTaxonomyNames() {
  const categories = useCategoriesList();
  const tags = useTagsList();
  const categoryNames = useMemo(
    () => Object.fromEntries(categories.data.map((row) => [row.id, row.name])),
    [categories.data],
  );
  const tagNames = useMemo(
    () => Object.fromEntries(tags.data.map((row) => [row.id, row.name])),
    [tags.data],
  );
  return { categoryNames, tagNames };
}

export function useBooks(): { data: BookRow[]; isLoading: boolean } {
  const { categoryNames, tagNames } = useBookTaxonomyNames();
  const q = useQuery({
    queryKey: ['books'],
    queryFn: async (): Promise<NovelGroupSummary[]> => {
      // @real — 非分页控件逐页取全量书组，名称复用公共缓存，不逐书请求统计。
      const groups: NovelGroupSummary[] = [];
      const maxPages = 200;
      for (let i = 1; i <= maxPages; i++) {
        const page = await booksApi.listOffsetPaged(i, 200);
        groups.push(...page.data);
        if (!page.hasMore || page.data.length === 0) break;
      }
      return groups;
    },
  });
  const data = useMemo(
    () =>
      (q.data ?? []).map((group) => {
        const member = selectSummaryDisplayLanguage(group);
        return adaptNovelToBookRow(group, {
          displayLanguage: member,
          categoryName: member ? (categoryNames[member.category_id] ?? '') : '',
          tagNames: (member?.tags ?? []).map((id) => tagNames[id] ?? '').filter(Boolean),
        });
      }),
    [q.data, categoryNames, tagNames],
  );
  return { data, isLoading: q.isLoading };
}

// ---------- Chapters ----------
// loading / 失败兜底：稳定模块常量（同一引用）——避免 `?? []` 每渲染新建空数组被消费方依赖时触发重渲染。
const CHAPTERS_FALLBACK: ChapterRow[] = [];

/**
 * 某个 per-locale novel 的章节列表（GET /api/v1/books/:novelId/chapters）→ adaptChapter。
 * per-locale 模型下章节挂在某一 per-locale novel_id 下（locale 由 novel_id 隐含）。
 *
 * **必须传当前语言行 novel_id**：章节集合按 per-locale novel 独立维护。
 * novelId 未就绪（如详情页冷加载）时 enabled=false 不发请求，
 * 读出口返稳定 fallback（不暴露其它书残留缓存）。queryKey 含 rootNovelId 隔离各书缓存。
 */
export function useChapters(novelId?: string | null): {
  data: ChapterRow[];
  isLoading: boolean;
} {
  const enabled = !!novelId;
  const q = useQuery({
    queryKey: ['chapters', novelId ?? null],
    enabled,
    queryFn: async (): Promise<ChapterRow[]> => {
      // @real — chaptersApi.list(novelId)（GET /api/v1/books/:id/chapters）→ adaptChapter
      if (!novelId) return CHAPTERS_FALLBACK;
      const chapters = await chaptersApi.list(novelId);
      return chapters.map(adaptChapter);
    },
  });
  // enabled=false 时无视缓存返稳定 fallback：避免切书瞬间读到上一本书残留的 ['chapters', null]
  return {
    data: enabled ? (q.data ?? CHAPTERS_FALLBACK) : CHAPTERS_FALLBACK,
    isLoading: enabled ? q.isLoading : false,
  };
}

// ---------- Book stats（ADM-BOK-S014 运营信息统计）----------
/** 书组运营统计：阅读用户 / 付费用户 / 付费章节（真实聚合 reading_progress / unlock_records）。 */
export type BookStatsData = {
  readers: number;
  payers: number;
  paidChapters: number;
};
const BOOK_STATS_FALLBACK: BookStatsData = { readers: 0, payers: 0, paidChapters: 0 };

/**
 * 书组运营统计（`GET /api/v1/book-groups/:groupId/stats`，A6 §19）。
 * 详情页 QuickStats / 数据 tab 消费真值；`groupId`（= `novel_groups.id`，dd#970）未就绪时
 * 不发请求、返 0 默认（不编造）。
 */
export function useBookStats(groupId?: string | null): {
  data: BookStatsData;
  isLoading: boolean;
} {
  const enabled = !!groupId;
  const q = useQuery({
    queryKey: ['book-stats', groupId ?? null],
    enabled,
    queryFn: async (): Promise<BookStatsData> => {
      // @real — booksApi.stats(groupId)（GET /api/v1/book-groups/:id/stats）
      if (!groupId) return BOOK_STATS_FALLBACK;
      const s = await booksApi.stats(groupId);
      return {
        // A6 §19 字段：reader_user_count 是**全时累计**阅读 UV（读 novel_groups.real_read_count
        // 单调计数器，不含运营基数）；付费两项按书组聚合有效 unlock_records
        readers: s.reader_user_count,
        payers: s.paid_user_count,
        paidChapters: s.paid_chapter_count,
      };
    },
  });
  return {
    data: enabled ? (q.data ?? BOOK_STATS_FALLBACK) : BOOK_STATS_FALLBACK,
    isLoading: enabled ? q.isLoading : false,
  };
}

/**
 * 书组详情：书组级资料 + 所有 per-locale 语言版本（`GET /api/v1/book-groups/:groupId`，A6 §15.2）
 * → NovelGroupDetail。详情页消费 rating（ADM-BOK-S016 运营展示评分，列表 summary 不带）+
 * 各语言行原始字段 + 书组级 `cn_title` / 原作信息 / 权属 / 展示基数三元组。
 *
 * **`groupId` 是 `novel_groups.id`**（dd#970）—— 不是「书组根 novel id」，也不是任何 novels 行 id。
 * 未就绪时不发请求。
 */
export function useBookDetail(groupId?: string | null): {
  data: import('@novel-admin/shared').NovelGroupDetail | null;
  isLoading: boolean;
} {
  const enabled = !!groupId;
  const q = useQuery({
    queryKey: ['book-detail', groupId ?? null],
    enabled,
    queryFn: async () => {
      // @real — booksApi.detail(groupId)（GET /api/v1/book-groups/:id）
      if (!groupId) return null;
      return booksApi.detail(groupId);
    },
  });
  return {
    data: enabled ? (q.data ?? null) : null,
    isLoading: enabled ? q.isLoading : false,
  };
}

// ---------- App 级覆盖（ADM-BOK-S012）----------
// loading / 失败 / 未就绪兜底：稳定模块常量（同一引用），避免每渲染新建空数组触发消费方重渲染。
const BOOK_OVERRIDES_FALLBACK: AppOverrideRow[] = [];

/**
 * 某本书（书组）的 App 级 HIDDEN/DELISTED 覆盖列表（GET /api/v1/books/:rootNovelId/overrides）。
 * ADM-BOK-S012「维护 App 级隐藏/下架列表」的契约读模型——override = HIDDEN/DELISTED 例外行（LISTED=删行回全局态）。
 * server 无「书在哪些 App 投放」成员关系端点，故 override 列表是唯一可消费的 per-app 状态事实源（不再 charCodeAt 伪造）。
 * rootNovelId 未就绪时不发请求；enabled=false 读出口返稳定 fallback（不暴露其它书残留缓存）。
 */
export function useBookOverrides(rootNovelId?: string | null): {
  data: AppOverrideRow[];
  isLoading: boolean;
} {
  const enabled = !!rootNovelId;
  const q = useQuery({
    queryKey: ['book-overrides', rootNovelId ?? null],
    enabled,
    queryFn: async (): Promise<AppOverrideRow[]> => {
      // @real — booksApi.listOverrides(rootNovelId)（GET /api/v1/books/:id/overrides）
      if (!rootNovelId) return BOOK_OVERRIDES_FALLBACK;
      return booksApi.listOverrides(rootNovelId);
    },
  });
  return {
    data: enabled ? (q.data ?? BOOK_OVERRIDES_FALLBACK) : BOOK_OVERRIDES_FALLBACK,
    isLoading: enabled ? q.isLoading : false,
  };
}

// 上下架 tab per-App 状态读模型未就绪 / 失败兜底：稳定空 apps + 全局 LISTED（同一引用，不每渲染新建）。
const BOOK_APP_STATUS_FALLBACK: BookAppStatusResponse = {
  // 兜底取**最保守**的一档而非 PUBLIC：读模型未就绪时把书显示成「全体可见」会让运营以为已在售，
  // 据此不去处理真实的下架诉求。UNLOCKED_ONLY = 不进发现面、已购仍可读（§6）。
  group_default_visibility: 'UNLOCKED_ONLY',
  novel_visibility: null,
  global_reason_i18n: null,
  apps: [],
};

/**
 * 某个 per-locale novel 上下架 tab 的三层读模型（GET /api/v1/books/:novelId/app-status；A6 §10/§11）。
 * 只读派生：group_default_visibility = 层①（书组维度）；novel_visibility = 层② 原值（null = 未覆盖）；
 * global_reason_i18n = 当前 novel 行的 status_reason_i18n（per-App 下架原因单点引用它）；
 * apps[] = 投放名单（novel.locale ∈ app.allowed_languages ∪ {'en'} 的未软删 App）∪ orphan override App，
 * 每行给 effective_visibility（三层解析）/ source_layer / has_override / changed_by / changed_at / launched_at。
 * novelId 未就绪不发请求；enabled=false 读出口返稳定 fallback（不暴露其它书残留缓存）。
 */
export function useBookAppStatus(novelId?: string | null): {
  data: BookAppStatusResponse;
  isLoading: boolean;
} {
  const enabled = !!novelId;
  const q = useQuery({
    queryKey: ['book-app-status', novelId ?? null],
    enabled,
    queryFn: async (): Promise<BookAppStatusResponse> => {
      // @real — booksApi.appStatus(novelId)（GET /api/v1/books/:id/app-status）
      if (!novelId) return BOOK_APP_STATUS_FALLBACK;
      return booksApi.appStatus(novelId);
    },
  });
  return {
    data: enabled ? (q.data ?? BOOK_APP_STATUS_FALLBACK) : BOOK_APP_STATUS_FALLBACK,
    isLoading: enabled ? q.isLoading : false,
  };
}

// loading / 失败 / 未就绪兜底：稳定模块常量（同一引用），避免每渲染 `?? []` 新建空数组触发消费方重渲染。
const BOOK_OPERATION_LOGS_FALLBACK: BookOperationLogItem[] = [];
const BOOK_SYSTEM_EVENT_LOGS_FALLBACK: SystemEventLogItem[] = [];

/**
 * 某个语言版本的运营操作 / 系统事件日志（GET /api/v1/books/:novelId/logs；ADM-BOK-S015）。
 * type=operation = admin_audit_logs（字段编辑/状态/下架原因/App 覆盖/章节操作）；
 * type=system = novel_system_events（定时发布/自动采集/状态机切换）。
 * offset 分页；from/to 时间窗下推服务端。novelId 未就绪不发请求，读出口返稳定 fallback（不暴露其它语言版本残留缓存）。
 */
export function useBookOperationLogs(
  novelId: string | undefined,
  query: BookOperationLogQuery & { type: 'system' },
  page: number,
  limit: number,
): OffsetPageView<SystemEventLogItem> & { isError: boolean; refetch: () => void };
export function useBookOperationLogs(
  novelId: string | undefined,
  query: BookOperationLogQuery & { type?: 'operation' },
  page: number,
  limit: number,
): OffsetPageView<BookOperationLogItem> & { isError: boolean; refetch: () => void };
export function useBookOperationLogs(
  novelId: string | undefined,
  query: BookOperationLogQuery,
  page: number,
  limit: number,
): OffsetPageView<BookOperationLogItem | SystemEventLogItem> & {
  isError: boolean;
  refetch: () => void;
};
export function useBookOperationLogs(
  novelId: string | undefined,
  query: BookOperationLogQuery,
  page: number,
  limit: number,
): OffsetPageView<BookOperationLogItem | SystemEventLogItem> & {
  isError: boolean;
  refetch: () => void;
} {
  const enabled = !!novelId;
  const q = useQuery({
    enabled,
    queryKey: [
      'book-logs',
      novelId ?? null,
      query.type ?? 'operation',
      query.from ?? '',
      query.to ?? '',
      page,
      limit,
    ],
    // @real — booksApi.listLogs(novelId, query, page, limit)（GET /api/v1/books/:id/logs?type&from&to&page&limit）
    queryFn: () =>
      booksApi.listLogs<BookOperationLogItem | SystemEventLogItem>(
        novelId as string,
        query,
        page,
        limit,
      ),
  });
  const fallback =
    query.type === 'system' ? BOOK_SYSTEM_EVENT_LOGS_FALLBACK : BOOK_OPERATION_LOGS_FALLBACK;
  return {
    data: enabled ? (q.data?.data ?? fallback) : fallback,
    isLoading: enabled ? q.isLoading : false,
    // 暴露 isError：消费方据此区分「真·空审计」与「加载失败」，不把请求失败渲染成「暂无日志」误导运营。
    isError: enabled ? q.isError : false,
    // 暴露 refetch：错误态给运营「重试」入口（retry:1 耗尽 + 不 refetchOnFocus，否则卡死错误页）。
    refetch: () => {
      void q.refetch();
    },
    total: q.data?.total ?? 0,
    hasMore: q.data?.hasMore ?? false,
  };
}

// ---------- Categories / Tags（真实行，含 id，用于编辑写动作）----------
const CAT_TAG_FALLBACK: CatTagRow[] = [];

/** 分类列表（GET /api/v1/categories）→ adaptCategory（含真实 id，编辑写动作用）。 */
export function useCategoriesList(): { data: CatTagRow[]; isLoading: boolean } {
  const q = useQuery({
    queryKey: ['categories'],
    staleTime: 60_000,
    queryFn: async (): Promise<CatTagRow[]> => {
      // @real — categoriesApi.list()（GET /api/v1/categories）→ adaptCategory
      const dtos = await categoriesApi.list();
      return dtos.map(adaptCategory);
    },
  });
  return { data: q.data ?? CAT_TAG_FALLBACK, isLoading: q.isLoading };
}

/** 标签列表（GET /api/v1/tags）→ adaptTag（含真实 id，编辑写动作用）。 */
export function useTagsList(): { data: CatTagRow[]; isLoading: boolean } {
  const q = useQuery({
    queryKey: ['tags'],
    staleTime: 60_000,
    queryFn: async (): Promise<CatTagRow[]> => {
      // @real — tagsApi.list()（GET /api/v1/tags）→ adaptTag
      const dtos = await tagsApi.list();
      return dtos.map(adaptTag);
    },
  });
  return { data: q.data ?? CAT_TAG_FALLBACK, isLoading: q.isLoading };
}

// ---------- Users ----------
export function useUsersPage(params: {
  page: number;
  pageSize: number;
  status?: UserListQuery['status'];
  appId?: string | null;
  /** 服务端下推筛选：q 关键字 / country 多选（ISO alpha-2）/ language 当前内容语言多选（A3 §5.1）。plan 后端未支持，不传。 */
  filters?: {
    q?: string;
    country?: string[];
    language?: string[];
    tag_ids?: string[];
    paid_status?: 'paid' | 'unpaid';
  };
}): { data: UserRow[]; total: number; isLoading: boolean } {
  const q = useQuery({
    queryKey: [
      'users',
      'page',
      params.page,
      params.pageSize,
      params.status ?? 'all',
      params.appId ?? 'all',
      // 筛选进 queryKey：改 q / country 触发重取（不再服务陈旧页）。country 数组排序后 join 稳定 key。
      params.filters?.q ?? '',
      [...(params.filters?.country ?? [])].sort().join(','),
      [...(params.filters?.language ?? [])].sort().join(','),
      [...(params.filters?.tag_ids ?? [])].sort().join(','),
      params.filters?.paid_status ?? '',
    ],
    queryFn: async (): Promise<{ rows: UserRow[]; total: number }> => {
      // @real — GET /api/v1/users?page&limit&status&app_id&q&country&language（后端分页 + 服务端筛选）→ adaptUserListItem
      const query: UserListQuery = {
        page: params.page,
        limit: params.pageSize,
        ...(params.status ? { status: params.status } : {}),
        ...(params.appId ? { app_id: params.appId } : {}),
        ...(params.filters?.q ? { q: params.filters.q } : {}),
        ...(params.filters?.country && params.filters.country.length > 0
          ? { country: params.filters.country }
          : {}),
        ...(params.filters?.language && params.filters.language.length > 0
          ? { language: params.filters.language }
          : {}),
        ...(params.filters?.tag_ids && params.filters.tag_ids.length > 0
          ? { tag_ids: params.filters.tag_ids }
          : {}),
        ...(params.filters?.paid_status ? { paid_status: params.filters.paid_status } : {}),
      };
      const [res, apps] = await Promise.all([usersApi.list(query), listApps()]);
      const appNameById: Record<string, string> = {};
      for (const a of apps) appNameById[a.id] = a.name;
      return {
        total: res.total,
        rows: (res.items ?? []).map((item) =>
          adaptUserListItem(item, { appName: item.app_id ? appNameById[item.app_id] : undefined }),
        ),
      };
    },
  });
  return { data: q.data?.rows ?? [], total: q.data?.total ?? 0, isLoading: q.isLoading };
}

/** 全局用户标签定义（ADM-USR-S008） */
export function useUserTagsPage(params: { page: number; pageSize: number }): {
  data: UserTagDto[];
  total: number;
  isLoading: boolean;
  isError: boolean;
} {
  const q = useQuery({
    queryKey: ['user-tags', 'page', params.page, params.pageSize],
    queryFn: () => userTagsApi.list(params.page, params.pageSize),
  });
  return {
    data: q.data?.data ?? [],
    total: q.data?.total ?? 0,
    isLoading: q.isLoading,
    isError: q.isError,
  };
}

export function useUsers(): { data: UserRow[]; isLoading: boolean } {
  const q = useQuery({
    queryKey: ['users'],
    queryFn: async (): Promise<UserRow[]> => {
      // @real — GET /api/v1/users（分页 offset，首页拉 100 条）→ adaptUserListItem（data.items 映射）
      // 真 DTO 缺 appName/plan：N+1 listApps() 建 app_id→名 map 还原 appName；
      // plan 无统一事实源（订阅在 Adapty）→ adapter 落空串；lang 取 DTO language（当前内容语言）。
      const [res, apps] = await Promise.all([usersApi.list({ page: 1, limit: 100 }), listApps()]);
      const appNameById: Record<string, string> = {};
      for (const a of apps) appNameById[a.id] = a.name;
      return (res.items ?? []).map((item) =>
        adaptUserListItem(item, { appName: item.app_id ? appNameById[item.app_id] : undefined }),
      );
    },
  });
  return { data: q.data ?? [], isLoading: q.isLoading };
}

/** 用户 KPI 视图：/users/stats 的累计/活跃/新增 + 封禁/风控真实总数（取自列表 status 筛选的 total）。 */
export type UserStatsView = UsersStatsResponse & { banned_users: number; risk_users: number };

/**
 * 用户统计 KPI（ADM-USR-S001）：累计 / 活跃(30天) / 新增(30天) + 封禁 / 风控 —— 平台级全局口径。
 * @real — usersApi.stats()（GET /api/v1/users/stats，聚合 users/user_sessions，30 天窗口）+
 *   GET /api/v1/users?status=banned|risk&limit=1 取 `total` 作封禁/风控**真实全量计数**。
 * **封禁/风控不从已加载用户列表派生**：列表首页只取 100 条，>100 会漏计（codex #115）；改用列表
 * status 筛选的 total 字段拿真实总数。
 * **平台级全局口径（不接顶栏 app-scope）**：本页 list（useUsers 无 app_id）/ 趋势（useDashboard）/ tab
 * 计数均为全局，且页内用自有 appFilter（非全局 AppSwitcher）；KPI 若按全局 AppSwitcher 取会与其余全局
 * 数据形成混合口径误导运营（codex #115 七轮）。ADM-USR-S001 的「按 App 维度」由 /users/stats?app_id
 * 端点能力满足，整页统一接全局 AppSwitcher（list+KPI+charts）是独立重构，留作 follow-up。
 * **加载中 / 失败返 data=null**（不返 0 兜底）：否则后端故障被伪装成真 0 误导运营，页面显「—」（四/五轮）。
 */
export function useUserStats(): { data: UserStatsView | null; isLoading: boolean } {
  const q = useQuery({
    queryKey: ['user-stats'],
    queryFn: async (): Promise<UserStatsView> => {
      const countByStatus = (status: UserListQuery['status']) =>
        usersApi.list({ status, page: 1, limit: 1 }).then((r) => r.total ?? 0);
      const [stats, banned, risk] = await Promise.all([
        usersApi.stats(),
        countByStatus('banned'),
        countByStatus('risk'),
      ]);
      return { ...stats, banned_users: banned, risk_users: risk };
    },
  });
  // q.isError 时返 null（即便有上次缓存数据）：refetch 失败（如 ban/unban 后失效重取报错）TanStack 会保留
  // 旧 q.data，若直接透出会让卡片显示 stale 计数而非「—」失败态（codex #115 五轮）。
  return { data: q.isError ? null : (q.data ?? null), isLoading: q.isLoading };
}

/** 用户详情（ADM-USR）：GET /users/:id → 订阅状态 / 最近登录 IP / 风控等富集字段。enabled 受抽屉开关控（关时不取）。 */
export function useUserDetail(
  userId: string,
  enabled = true,
): {
  data: AdminUserDetailResponse | null;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  isFetching: boolean;
  isFetchedAfterMount: boolean;
  refetch: () => void;
} {
  const q = useQuery({
    queryKey: ['user-detail', userId],
    enabled,
    // 标签人工授权必须等本次打开后真实详情确认，不能沿用仍新鲜的旧缓存。
    refetchOnMount: 'always',
    queryFn: () => usersApi.detail(userId),
    // 业务错误（HTTP 422，如 RESOURCE_NOT_FOUND）是确定性结果 → 不重试（即时反馈 + 省一次后端调用，
    // 归属预览尤其需要即时区分「用户不存在」）；传输 / 5xx 仍兜一次瞬时故障。
    retry: (count, err) => !(err instanceof AdminApiError && err.httpStatus === 422) && count < 1,
  });
  return {
    data: q.data ?? null,
    isLoading: q.isLoading,
    isError: q.isError,
    error: q.error,
    isFetching: q.isFetching,
    isFetchedAfterMount: q.isFetchedAfterMount,
    refetch: () => {
      void q.refetch();
    },
  };
}

/** 用户充值记录（ADM-USR 详情抽屉「充值记录」tab）：首页 50 条 + 真实总数；tab 激活才取。 */
export function useUserRecharges(userId: string, enabled = true) {
  const q = useQuery({
    queryKey: ['user-recharges-v2', userId],
    enabled,
    retry: false,
    queryFn: () => usersApi.listRecharges(userId, 1, 50),
  });
  const serviceUpdating = q.error instanceof AdminApiError && q.error.httpStatus === 404;
  return {
    data: q.data?.data ?? [],
    total: q.data?.total ?? 0,
    isLoading: q.isLoading,
    isError: q.isError,
    serviceUpdating,
    error: q.error,
    reload: q.refetch,
  };
}

/** 用户反馈记录（ADM-USR 详情抽屉「反馈」tab）：首页 50 条 + 真实总数；tab 激活才取。 */
export function useUserFeedback(
  userId: string,
  enabled = true,
): { data: FeedbackListItem[]; total: number; isLoading: boolean } {
  const q = useQuery({
    queryKey: ['user-feedback-records', userId],
    enabled,
    queryFn: () => usersApi.listFeedback(userId, 1, 50),
  });
  return { data: q.data?.data ?? [], total: q.data?.total ?? 0, isLoading: q.isLoading };
}

/** 用户详情记录 tab 的每页条数（A3 §7.3 limit 默认 50）。 */
export const USER_RECORD_TAB_PAGE_SIZE = 50;

/** 用户详情记录 tab 通用返回形：当前页 + 真实总数 + 加载 / 失败态（失败可重载）。 */
export type UserRecordTabQuery<T> = {
  data: T[];
  total: number;
  /** 尚无任何数据（首次加载或离线暂停）—— 与「确实为空」区分，避免离线时显示假空态 */
  isPending: boolean;
  /** 翻页时新页未到、仍展示上一页数据（keepPreviousData 占位）—— 界面须标注，不能当新页展示 */
  isPlaceholderData: boolean;
  /**
   * 本页结果已落定：请求成功、没有在途或被暂停的请求、也不是占位 —— 只有这时 total 才可信，
   * 可据以判断页码越界（离线暂停时 isFetching 为 false，但结果并未落定）
   */
  isResultSettled: boolean;
  isError: boolean;
  error: unknown;
  /** 旧 Worker 尚无该 v2 路由（HTTP 404）—— 显示「服务更新中」，不当作加载失败、不回落 v1；仅 v2 子资源设置 */
  serviceUpdating?: boolean;
  reload: () => void;
};

function toUserRecordTabQuery<T>(q: {
  data?: { data: T[]; total: number };
  isPending: boolean;
  isPlaceholderData: boolean;
  status: 'pending' | 'error' | 'success';
  fetchStatus: 'fetching' | 'paused' | 'idle';
  isError: boolean;
  error: unknown;
  refetch: () => unknown;
}): UserRecordTabQuery<T> {
  return {
    data: q.data?.data ?? [],
    total: q.data?.total ?? 0,
    isPending: q.isPending,
    isPlaceholderData: q.isPlaceholderData,
    isResultSettled: q.status === 'success' && q.fetchStatus === 'idle' && !q.isPlaceholderData,
    isError: q.isError,
    error: q.error,
    reload: () => {
      void q.refetch();
    },
  };
}

/** 用户章节阅读记录（详情抽屉「章节阅读记录」tab，A3 §7.3）：tab 激活才取，翻页保留上一页直到新页到达。 */
export function useUserChapterReadingRecords(
  userId: string,
  enabled = true,
  page = 1,
): UserRecordTabQuery<UserChapterReadingRecordItem> {
  const q = useQuery({
    queryKey: ['user-chapter-reading-records', userId, page],
    enabled,
    // 记录会被 C 端删改（清除阅读记录等），翻页 / 切回总要重取，不信 30s 内缓存里的旧 total
    staleTime: 0,
    placeholderData: keepPreviousData,
    queryFn: () => usersApi.listChapterReadingRecords(userId, page, USER_RECORD_TAB_PAGE_SIZE),
  });
  return toUserRecordTabQuery(q);
}

/** 用户章节解锁记录（详情抽屉「章节解锁记录」tab，A3 §7.3）：tab 激活才取，翻页保留上一页直到新页到达。 */
export function useUserChapterUnlockRecords(
  userId: string,
  enabled = true,
  page = 1,
): UserRecordTabQuery<UserChapterUnlockRecordItem> {
  const q = useQuery({
    queryKey: ['user-chapter-unlock-records', userId, page],
    enabled,
    // 记录会被 C 端删改（清除阅读记录等），翻页 / 切回总要重取，不信 30s 内缓存里的旧 total
    staleTime: 0,
    placeholderData: keepPreviousData,
    queryFn: () => usersApi.listChapterUnlockRecords(userId, page, USER_RECORD_TAB_PAGE_SIZE),
  });
  return toUserRecordTabQuery(q);
}

/** 用户小说阅读记录（详情抽屉「小说阅读记录」tab，A3 §7.3）：tab 激活才取，翻页保留上一页直到新页到达。 */
export function useUserNovelReadingRecords(
  userId: string,
  enabled = true,
  page = 1,
): UserRecordTabQuery<UserNovelReadingRecordItem> {
  const q = useQuery({
    queryKey: ['user-novel-reading-records', userId, page],
    enabled,
    // 记录会被 C 端删改（清除阅读记录等），翻页 / 切回总要重取，不信 30s 内缓存里的旧 total
    staleTime: 0,
    placeholderData: keepPreviousData,
    queryFn: () => usersApi.listNovelReadingRecords(userId, page, USER_RECORD_TAB_PAGE_SIZE),
  });
  return toUserRecordTabQuery(q);
}

/**
 * 用户消费记录（详情抽屉「消费记录」tab，A3 §7.2 v2 资产维度）：tab 激活才取，翻页保留上一页直到新页到达。
 * 旧 Worker 无 v2 路由时 404 → serviceUpdating。
 */
export function useUserConsumes(
  userId: string,
  enabled = true,
  page = 1,
): UserRecordTabQuery<UserConsumeRecordItem> {
  const q = useQuery({
    queryKey: ['user-consumes-v2', userId, page],
    enabled,
    // 新消费随时追加，翻页 / 切回总要重取，不信 30s 内缓存里的旧 total
    staleTime: 0,
    placeholderData: keepPreviousData,
    // 404（旧 Worker 无 v2 路由）与 422（RESOURCE_NOT_FOUND 等业务结论）都是确定性结果，重试无益
    retry: (count, err) =>
      !(err instanceof AdminApiError && (err.httpStatus === 404 || err.httpStatus === 422)) &&
      count < 1,
    queryFn: () => usersApi.listConsumes(userId, page, USER_RECORD_TAB_PAGE_SIZE),
  });
  return {
    ...toUserRecordTabQuery(q),
    serviceUpdating: q.error instanceof AdminApiError && q.error.httpStatus === 404,
  };
}

// ---------- Refunds ----------
/** 退款触发类型 / 处理结果 机器码 → 原型中文（页面只读展示）。 */
const REFUND_TRIGGER_ZH: Record<RefundRecordResponse['trigger'], string> = {
  PASSIVE: '被动退款',
  ACTIVE: '主动退款',
};
const REFUND_RESULT_ZH: Record<RefundRecordResponse['final_result'], string> = {
  COMPLETED: '已退款',
  PENDING: '处理中',
  REJECTED: '已驳回',
};

/** 退款 DTO → 页面行（appNameById 用于把 app_id 还原成中文 App 名）。 */
function adaptRefundRecord(
  r: RefundRecordResponse,
  appNameById: Record<string, string>,
): RefundRow {
  const appId = r.app_id ?? '';
  return {
    id: r.id,
    // 订单号接真：支付商交易号 vendor_transaction_id（运营识别口径），非内部行 UUID r.id。
    // nullish 兜底（灰度窗 web/Worker 分开部署：新 Pages 对旧 Worker 响应可能缺该字段 → undefined）→
    // 诚实占位 '—'，避免 orderId 为 undefined 时 FinancePage 搜索 .toLowerCase() 崩；不回退内部 UUID。
    environment: r.environment ?? 'Production',
    orderId: r.vendor_transaction_id ?? '—',
    userId: r.user_id,
    appId,
    appName: appId ? (appNameById[appId] ?? appId) : '',
    channel: '',
    amount: fmtCurrencyMinor(r.amount_minor_units, r.currency),
    currency: r.currency,
    reason: '',
    type: REFUND_TRIGGER_ZH[r.trigger] ?? r.trigger,
    status: REFUND_RESULT_ZH[r.final_result] ?? r.final_result,
    createdAt: r.refunded_at,
  };
}

const REFUNDS_FALLBACK: RefundRow[] = [];

/**
 * ADM-FIN-S003 退款记录（offset 分页 + 随 App scope 注入 app_id）。
 *
 * F1（跨 App 泄漏）：queryKey 带 appId、请求带 ?app_id → 单 App 视图只取该 App 退款，scope 切换隔离缓存 / 重取。
 * offset（cursor「加载更多」useInfiniteQuery → offset 分页器，对齐 books/users，dev-docs 40-分页与排序）：
 *   page/limit 直驱后端，total 来自 envelope 真实总数 → 跳任意页；scope 切换换 queryKey → 分页天然重置。
 * N+1：每页 listApps() 还原 appName（DTO 只给 app_id）。amount 原币 minor→major string；currency 取 DTO 原币（#119）。
 */
export function useRefundsPage(params: { page: number; limit: number }): OffsetPageView<RefundRow> {
  const appId = useAppScope((s) => s.appId);
  const q = useQuery({
    queryKey: ['refunds', 'page', appId, params.page, params.limit],
    queryFn: async (): Promise<OffsetPagedResult<RefundRow>> => {
      // @real — GET /api/v1/finance/refunds?app_id&page&limit（最新在前，offset）→ RefundRow
      const [page, apps] = await Promise.all([
        financeApi.refunds(appIdQuery(appId), params.page, params.limit),
        listApps(),
      ]);
      const appNameById: Record<string, string> = {};
      for (const a of apps) appNameById[a.id] = a.name;
      return { ...page, data: page.data.map((r) => adaptRefundRecord(r, appNameById)) };
    },
  });
  return {
    data: q.data?.data ?? REFUNDS_FALLBACK,
    isLoading: q.isLoading,
    total: q.data?.total ?? 0,
    hasMore: q.data?.hasMore ?? false,
  };
}

// ---------- Transaction import attribution queue (ADM-FIN-S009) ----------
/** 待归属队列行：shared DTO + appName 富集（app_id → 中文 App 名，直用 shared 契约字段口径）。 */
export type TxnImportQueueRow = TransactionImportQueueRow & { appName: string };

const TXN_IMPORT_QUEUE_FALLBACK: TxnImportQueueRow[] = [];

/**
 * ADM-FIN-S009 交易导入待归属队列（offset 分页 + 随 App scope 注入 app_id）。
 * queryKey 带 appId + 归属状态 + lineage → scope / 状态 / 过滤切换换 key 天然重取 + 分页重置；
 * N+1 listApps() 还原 appName（DTO 只给 app_id）。
 * 金额 base_amount_local 为原币本币额 raw 字符串（非 minor units，dev-docs 60-订阅 §8 金融解析单源 C 端）。
 *
 * `originalTransactionId` 是**服务端**等值过滤（A1 §10.0），不是页内客户端筛选 —— 台账侧
 * `permanent_ambiguous_lineage` 行没有 `transaction_id`，运营只能以 lineage 跨页定位队列行。
 */
export function useTransactionImportQueue(params: {
  attributionStatus: TransactionImportAttributionStatus;
  originalTransactionId?: string;
  page: number;
  limit: number;
}): OffsetPageView<TxnImportQueueRow> & { isError: boolean } {
  const appId = useAppScope((s) => s.appId);
  const q = useQuery({
    queryKey: [
      'transaction-imports',
      appId,
      params.attributionStatus,
      params.originalTransactionId ?? '',
      params.page,
      params.limit,
    ],
    queryFn: async (): Promise<OffsetPagedResult<TxnImportQueueRow>> => {
      // @real — GET /api/v1/finance/transaction-imports?attribution_status&app_id&original_transaction_id&page&limit → TransactionImportQueueRow
      const [page, apps] = await Promise.all([
        financeApi.transactionImports({
          attribution_status: params.attributionStatus,
          app_id: appIdQuery(appId),
          original_transaction_id: params.originalTransactionId || undefined,
          page: params.page,
          limit: params.limit,
        }),
        listApps(),
      ]);
      const appNameById: Record<string, string> = {};
      for (const a of apps) appNameById[a.id] = a.name;
      return {
        ...page,
        data: page.data.map((r) => ({ ...r, appName: appNameById[r.app_id] ?? r.app_id })),
      };
    },
  });
  return {
    data: q.data?.data ?? TXN_IMPORT_QUEUE_FALLBACK,
    isLoading: q.isLoading,
    total: q.data?.total ?? 0,
    hasMore: q.data?.hasMore ?? false,
    isError: q.isError,
  };
}

// ---------- Adapty event manual review queue (ADM-FIN-S010) ----------
/** 事件闭环队列行：shared DTO + appName 富集（app_id → App 名）。 */
export type EventReviewQueueRow = AdaptyEventReviewQueueRow & { appName: string };

const EVENT_REVIEW_QUEUE_FALLBACK: EventReviewQueueRow[] = [];

/**
 * ADM-FIN-S010 webhook 事件人工待办（offset + App scope 注入 app_id）。
 * queryKey 带 appId + 状态/准入/错误码 → 切换换 key 天然重取；N+1 listApps() 还原 appName。
 * 行内快照直展示，不反查源事件。
 */
export function useAdaptyEventReviews(params: {
  resolutionStatus: AdaptyEventReviewResolutionStatus;
  admissionKind?: AdaptyEventReviewAdmissionKind;
  errorCode?: AdaptyEventReviewErrorCode;
  page: number;
  limit: number;
}): OffsetPageView<EventReviewQueueRow> & { isError: boolean } {
  const appId = useAppScope((s) => s.appId);
  const q = useQuery({
    queryKey: [
      'adapty-event-reviews',
      appId,
      params.resolutionStatus,
      params.admissionKind ?? '',
      params.errorCode ?? '',
      params.page,
      params.limit,
    ],
    queryFn: async (): Promise<OffsetPagedResult<EventReviewQueueRow>> => {
      // @real — GET /api/v1/finance/adapty-event-reviews → AdaptyEventReviewQueueRow
      // appName 是展示富集：listApps 失败回退 UUID，不挡闭环主路径（reviews 成功即可确认）。
      const [pageSettled, appsSettled] = await Promise.allSettled([
        financeApi.adaptyEventReviews({
          resolution_status: params.resolutionStatus,
          admission_kind: params.admissionKind,
          error_code: params.errorCode,
          app_id: appIdQuery(appId),
          page: params.page,
          limit: params.limit,
        }),
        listApps(),
      ]);
      if (pageSettled.status === 'rejected') throw pageSettled.reason;
      const appNameById: Record<string, string> = {};
      if (appsSettled.status === 'fulfilled') {
        for (const a of appsSettled.value) appNameById[a.id] = a.name;
      }
      return {
        ...pageSettled.value,
        data: pageSettled.value.data.map((r) => ({
          ...r,
          appName: appNameById[r.app_id] ?? r.app_id,
        })),
      };
    },
  });
  return {
    data: q.data?.data ?? EVENT_REVIEW_QUEUE_FALLBACK,
    isLoading: q.isLoading,
    total: q.data?.total ?? 0,
    hasMore: q.data?.hasMore ?? false,
    isError: q.isError,
  };
}

/** 退款单运营备注（ADM-FIN-S003 详情抽屉）：抽屉打开即取（GET /finance/refunds/:id/note）；未录入返回空 note。 */
export function useRefundNote(
  refundId: string,
  enabled = true,
  environment: RefundRecordResponse['environment'] = 'Production',
) {
  const q = useQuery({
    queryKey: ['refund-note', refundId, environment],
    enabled,
    queryFn: () => financeApi.getRefundNote(refundId, environment),
  });
  return { data: q.data ?? null, isLoading: q.isLoading };
}

// ---------- Feedback ----------
/**
 * 翻页累积安全上限（adversarial F7）：server cursor keyset 每页上限 200（lib/cursor AdminPageQuerySchema），
 * 累积 25 页 = 5000 条覆盖 MVP 反馈量级。命中上限时停止（hasMore 仍可能为真）→ 由 useFeedback 返回
 * truncated 标记，页面据此**诚实标注** count / 列表为「已加载范围」，不假装全量（F7：靠后 OPEN 工单不静默消失，
 * 但若真超 5000 条则明示截断，而非默默漏统计）。
 */
const FEEDBACK_MAX_PAGES = 25;

/**
 * 反馈列表累积视图：rows = 跨页累积的全部工单；truncated = 是否因命中 FEEDBACK_MAX_PAGES 上限提前停止
 * （server 仍有更多页未拉）。页面据 truncated 决定 count / 列表是否标注「仅已加载范围」。
 */
export type FeedbackListView = {
  rows: FeedbackRow[];
  truncated: boolean;
};

const FEEDBACK_LIST_FALLBACK: FeedbackListView = { rows: [], truncated: false };

export function useFeedback(): { data: FeedbackListView; isLoading: boolean } {
  // ADM-FBK-S001：App 维度筛选走顶栏 app-scope（P1-web-design-system §4，不各页自造 dropdown）；
  // 'all' → appIdQuery 转 undefined（不传 app_id = 全 App）。appId 进 queryKey：切 App 重新累积分页。
  const appId = useAppScope((s) => s.appId);
  const q = useQuery({
    queryKey: ['feedback', appId],
    queryFn: async (): Promise<FeedbackListView> => {
      // @real — feedbackApi.listOffsetPaged(undefined, page, appIdQuery(appId))（GET /api/v1/feedback?app_id&page，
      // offset okOffsetPaged）→ adaptFeedbackListItem。列表 DTO 现含 app_id（catch-up §1，仅 admin 暴露）；
      // 提交用户富集仍不逐行 N+1 拉 user 详情（避免 O(n)）→ adapter 用 user_id 最简占位、appName 回退 platform。
      // F7：收件箱按 page 递增**累积全部页**（不只首页）—— tab 计数 / 客户端筛选 / master-detail 需全集，
      // 否则记录超首页后靠后 OPEN 工单从列表 + 待回复 count 双双消失（SLA miss）。无 status 筛选传入（页面 tab 在
      // 已累积全集上客户端筛）；累积到 FEEDBACK_MAX_PAGES 上限则停并标 truncated，页面诚实标注 count 范围（不静默漏统计）。
      const items: FeedbackListItem[] = [];
      let truncated = false;
      for (let i = 1; i <= FEEDBACK_MAX_PAGES; i++) {
        const page = await feedbackApi.listOffsetPaged(undefined, i, appIdQuery(appId), 200);
        items.push(...page.data);
        if (!page.hasMore || page.data.length === 0) break;
        // 已是最后一轮但 server 仍报有更多 → 数据被 FEEDBACK_MAX_PAGES 上限截断
        if (i === FEEDBACK_MAX_PAGES) truncated = true;
      }
      return { rows: items.map((item) => adaptFeedbackListItem(item)), truncated };
    },
  });
  return { data: q.data ?? FEEDBACK_LIST_FALLBACK, isLoading: q.isLoading };
}

// ---------- Feedback 详情 ----------
/**
 * 反馈详情视图数据：detail = 真 DTO（正文 description / 回复 thread replies / 中文翻译 translation /
 * contact_email / language 等）；user = N+1 拉取的提交用户详情（display_name 等富集字段，
 * 拉取失败不阻塞详情 → null，页面回退 user_id 展示）；feedbackAppName 按 detail.app_id 从 apps 表解析，
 * 缺失时为 null，页面显占位不杜撰。
 */
export type FeedbackDetailView = {
  detail: FeedbackDetailResponse;
  user: AdminUserDetailResponse | null;
  feedbackAppName: string | null;
};

/** 页面消费的 feedback 详情 DTO 类型 re-export（页面只从 '~/data/queries' 取类型） */
export type {
  FeedbackDetailResponse,
  FeedbackReplyResponse,
  FeedbackTranslation,
} from '@novel-admin/shared';

export function useFeedbackDetail(id: string | undefined): {
  data: FeedbackDetailView | null;
  isLoading: boolean;
} {
  const q = useQuery({
    queryKey: ['feedback-detail', id],
    enabled: Boolean(id),
    queryFn: async (): Promise<FeedbackDetailView> => {
      // @real — feedbackApi.detail(id)（GET /api/v1/feedback/:id）→ 详情 DTO 直接消费
      // （description / replies / translation / status_log 以 DTO 实际字段为准；status_log 已接真
      //   = 流转历史时间线；attachments 仍 NOT-in-scope，见 docs/plans/2026-06-12 §五）。
      // N+1：usersApi.detail(user_id) 富集提交用户（display_name）；listApps() 建 app_id→名 map 仅作兜底。
      // 详情头 App chip 优先用详情 DTO 已接真的 detail.app_name（apps.name，server left join 解析）——
      // listApps() best-effort 且过滤软删 App，若失败/缺该 App 仍能显真名；两者皆缺再回退 null。
      const detail = await feedbackApi.detail(id as string);
      const [userSettled, appsSettled] = await Promise.allSettled([
        detail.user_id === null ? Promise.resolve(null) : usersApi.detail(detail.user_id),
        listApps(),
      ]);
      const user = userSettled.status === 'fulfilled' ? userSettled.value : null;
      const appNameById: Record<string, string> = {};
      if (appsSettled.status === 'fulfilled') {
        for (const app of appsSettled.value) appNameById[app.id] = app.name;
      }
      const feedbackAppName =
        detail.app_name ?? (detail.app_id ? (appNameById[detail.app_id] ?? null) : null);
      return { detail, user, feedbackAppName };
    },
  });
  return { data: q.data ?? null, isLoading: q.isLoading };
}

// ---------- Push templates ----------
export function usePushTemplates(): { data: PushTemplate[]; isLoading: boolean } {
  const q = useQuery({
    queryKey: ['push-templates'],
    queryFn: async (): Promise<PushTemplate[]> => {
      // @real — pushApi.listTemplates()（GET /api/v1/push/templates）→ adaptPushTemplates
      const dtos = await pushApi.listTemplates();
      return adaptPushTemplates(dtos);
    },
  });
  return { data: q.data ?? [], isLoading: q.isLoading };
}

// ---------- Push history ----------
export function usePushHistoryPage(params: {
  page: number;
  limit: number;
  /**
   * 服务端下推筛选（ADM-PUSH-S005）：status/app_id/task_type/operator 多值，target_scope 单值。
   * operator 按 created_by(uuid) 下推（跨全量过滤）。标题搜索因 push_tasks 无 title 列仍前端当前页过滤。
   * offset 分页（admin 列表默认 offset，dev-docs 40-分页与排序）：filterKey 进 queryKey 保证 filter 变化时
   * 整条 query 重取；页面须在 filter 变化时把 page 重置回第一页（避免越界空页）。
   */
  filters?: ListPushTasksQuery;
}): OffsetPageView<PushHistoryRow> {
  const q = useQuery({
    queryKey: ['push-history', 'page', params.page, params.limit, pushFilterKey(params.filters)],
    queryFn: async (): Promise<OffsetPagedResult<PushHistoryRow>> => {
      // @real — pushApi.listTasks(page, limit, filters)（GET /api/v1/push/tasks?page&limit&<filters>）→ 当前后端页 + 真实 total
      const apps = await listApps();
      const appNameById: Record<string, string> = {};
      for (const a of apps) appNameById[a.id] = a.name;
      const page = await pushApi.listTasks(params.page, params.limit, params.filters);
      return { ...page, data: adaptPushTasks(page.data, appNameById) };
    },
  });
  return {
    data: q.data?.data ?? [],
    isLoading: q.isLoading,
    total: q.data?.total ?? 0,
    hasMore: q.data?.hasMore ?? false,
  };
}

/**
 * usePushHistory 返回值：累积全量行 + 截断标记。
 * truncated=true 表示命中 maxPages 上限但 server 仍报 hasMore——返回的是**部分**数据而非完整集，
 * 页面据此显式提示「仅已加载 N 条」，不把截断当完整（计数 / 筛选基于已加载子集，需让运营知情）。
 */
export function usePushHistory(): {
  data: PushHistoryRow[];
  isLoading: boolean;
  truncated: boolean;
} {
  const q = useQuery({
    queryKey: ['push-history'],
    queryFn: async (): Promise<{ rows: PushHistoryRow[]; truncated: boolean }> => {
      // @real — pushApi.listTasks(page)（GET /api/v1/push/tasks）→ adaptPushTasks
      // 翻页累积：GET /push/tasks 是 offset 分页（okOffsetPaged，默认 limit=200/上限 200），按 page 递增累积取全量
      //   （hasMore=false 停）。maxPages 上限兜底，防 server 异常时死循环（单页 ≤200，50 页足够覆盖 MVP 任务量级）。
      //   命中上限且 server 仍 hasMore → 标 truncated，由页面提示「仅已加载」，不假装完整。
      // N+1：listApps() 建 app_id→名 map，把 target_app_id 还原成中文 scope 名（DTO 只给 UUID）。
      // 缺字段（opened 打开数 / operator 操作人）真 API 无 → adapter 内落 0 / 空串。
      const apps = await listApps();
      const appNameById: Record<string, string> = {};
      for (const a of apps) appNameById[a.id] = a.name;

      const rows: PushHistoryRow[] = [];
      let truncated = false;
      const maxPages = 50;
      for (let i = 1; i <= maxPages; i++) {
        const page = await pushApi.listTasks(i, 200);
        rows.push(...adaptPushTasks(page.data, appNameById));
        if (!page.hasMore || page.data.length === 0) break;
        // 已是最后一轮但 server 仍报有更多 → 数据被 maxPages 上限截断
        if (i === maxPages) truncated = true;
      }
      return { rows, truncated };
    },
  });
  return {
    data: q.data?.rows ?? [],
    isLoading: q.isLoading,
    truncated: q.data?.truncated ?? false,
  };
}

// ---------- Banners ----------
// 稳定空 fallback 常量（checklist #1：禁每渲染 `?? []` 新建空数组 → 下游 effect 死循环）
const BANNERS_FALLBACK: Banner[] = [];
const HOME_BLOCKS_FALLBACK: HomeBlock[] = [];
const FAQS_FALLBACK: Faq[] = [];
export function useBanners(
  scopeAppId?: string,
  language = 'en',
): {
  data: Banner[];
  isLoading: boolean;
  /** 查询已成功返回：区分「确实没有位」与「还没查到 / 查失败」——两者的 data 都是空数组 */
  isSuccess: boolean;
  /** 重试耗尽后为 true；与 isSuccess 一起用，才能把「还在读」与「读不到」分开 */
  isError: boolean;
  configSource: OpsConfigSource;
} {
  const storeAppId = useAppScope((s) => s.appId);
  const appId = scopeAppId ?? storeAppId;
  const q = useQuery({
    queryKey: ['banners', appId, language],
    queryFn: async () => {
      // @real — GET /api/v1/ops/banners?language= 单层 envelope
      const apps = await listApps();
      const appNameById: Record<string, string> = {};
      for (const a of apps) appNameById[a.id] = a.name;
      const response = await opsApi.listBanners(appId === 'all' ? null : appId, language);
      const slideCountById: Record<string, number> = {};
      const slideCounts = await Promise.allSettled(
        response.items.map(async (b) => ({
          id: b.id,
          n: (await opsApi.listSlides(b.id, language)).length,
        })),
      );
      for (const r of slideCounts) {
        if (r.status === 'fulfilled') slideCountById[r.value.id] = r.value.n;
      }
      return {
        items: response.items.map((b) => fromBanner(b, { appNameById, slideCountById })),
        configSource: response.config_source,
      };
    },
  });
  return {
    data: q.data?.items ?? BANNERS_FALLBACK,
    isLoading: q.isLoading,
    isSuccess: q.isSuccess,
    isError: q.isError,
    configSource: q.data?.configSource ?? (appId === 'all' ? 'GLOBAL' : 'GLOBAL_INHERITED'),
  };
}

// ---------- Home blocks ----------
export function useHomeBlocks(
  scopeAppId?: string,
  language = 'en',
): { data: HomeBlock[]; isLoading: boolean; configSource: OpsConfigSource } {
  const storeAppId = useAppScope((s) => s.appId);
  const appId = scopeAppId ?? storeAppId;
  const q = useQuery({
    queryKey: ['home-blocks', appId, language],
    queryFn: async () => {
      // @real — opsApi.listSections(appId|null)（GET /api/v1/ops/home-sections）→ fromHomeSection
      // 「全部」= 全局栏（app_id 空）→ null；某 App = 该 App merge。
      const response = await opsApi.listSections(appId === 'all' ? null : appId, language);
      return { items: response.items.map(fromHomeSection), configSource: response.config_source };
    },
  });
  return {
    data: q.data?.items ?? HOME_BLOCKS_FALLBACK,
    isLoading: q.isLoading,
    configSource: q.data?.configSource ?? (appId === 'all' ? 'GLOBAL' : 'GLOBAL_INHERITED'),
  };
}

const LANGUAGE_TABS_FALLBACK: ConfigLanguageTabsResponse = {
  app_id: null,
  module: null,
  default_language: 'en',
  tabs: [],
};

/**
 * 运营配置统一语言 Tab（GET /ops/config-language-tabs）。
 * App 视图必须带 module（home_sections / faq）。身份表未落时 App 允许语言一律 GLOBAL_CONFIG_NOT_READY。
 */
export function useConfigLanguageTabs(
  appId: string,
  module: ConfigLanguageTabModule | null,
): { data: ConfigLanguageTabsResponse; isLoading: boolean; isError: boolean } {
  const scopedAppId = appId === 'all' ? null : appId;
  const q = useQuery({
    queryKey: ['ops-language-tabs', scopedAppId, module],
    queryFn: () =>
      opsApi.listLanguageTabs({
        appId: scopedAppId,
        module: scopedAppId ? module : null,
      }),
    enabled: !scopedAppId || module !== null,
  });
  return {
    data: q.data ?? LANGUAGE_TABS_FALLBACK,
    isLoading: q.isLoading,
    isError: q.isError,
  };
}

const SECTION_NOVELS_FALLBACK: HomeSectionNovelResponse[] = [];

/** 每周精选绑定行（GET /ops/home-sections/:id/novels）。表未落 → query error（503）。 */
export function useHomeSectionNovels(
  sectionId: string | null | undefined,
  locale: string,
): { data: HomeSectionNovelResponse[]; isLoading: boolean; isError: boolean; error: Error | null } {
  const q = useQuery({
    queryKey: ['home-section-novels', sectionId, locale],
    enabled: !!sectionId,
    queryFn: () => {
      if (!sectionId) return SECTION_NOVELS_FALLBACK;
      return opsApi.listSectionNovels(sectionId, locale);
    },
  });
  return {
    data: sectionId ? (q.data ?? SECTION_NOVELS_FALLBACK) : SECTION_NOVELS_FALLBACK,
    isLoading: q.isLoading,
    isError: q.isError,
    error: q.error ?? null,
  };
}

export type LocaleNovelCandidate = {
  novelId: string;
  locale: string;
  title: string;
  visibility: NovelVisibility;
  coverUrl: string;
  coverHue: number;
  bookCode: string | null;
  bookGroupCode: string | null;
};

const LOCALE_NOVELS_FALLBACK: LocaleNovelCandidate[] = [];

function hueOf(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h % 360;
}

/** 某语言的 per-locale 书行候选（每周精选绑书）。走 GET /books?lang=。 */
export function useLocaleNovelCandidates(
  locale: string,
  search = '',
  enabled = true,
): {
  data: LocaleNovelCandidate[];
  isLoading: boolean;
} {
  const q = useQuery({
    queryKey: ['books', 'locale-candidates', locale, search.trim()],
    enabled: !!locale && enabled,
    queryFn: async (): Promise<LocaleNovelCandidate[]> => {
      const page = await booksApi.listOffsetPaged(1, 200, {
        lang: [locale],
        ...(search.trim() ? { q: search.trim() } : {}),
      });
      const rows: LocaleNovelCandidate[] = [];
      for (const group of page.data) {
        for (const member of group.languages) {
          if (member.locale !== locale) continue;
          rows.push({
            novelId: member.novel_id,
            locale: member.locale,
            title: member.title || group.original_title,
            // 候选行按**合成值**下发（层② 覆盖层①）：层② 为 null 是常态，直接回填会让
            // 「继承书组默认」的行在选择器里显示成空档位。
            visibility: visibilityDisplayOf(member.visibility, member.group_default_visibility)
              .value,
            coverUrl: member.cover_url,
            coverHue: hueOf(member.novel_id),
            bookCode: member.book_code ?? null,
            bookGroupCode: group.book_group_code ?? null,
          });
        }
      }
      return rows;
    },
  });
  return { data: q.data ?? LOCALE_NOVELS_FALLBACK, isLoading: q.isLoading };
}

// ---------- Home section strategies（推荐策略注册表，BlockDetail 下拉源）----------
// 稳定空 fallback（checklist #1：禁每渲染 `?? []` 新建空数组）。
const HOME_SECTION_STRATEGIES_FALLBACK: HomeSectionStrategyResponse[] = [];
/**
 * useHomeSectionStrategies — 推荐策略注册表（GET /ops/home-section-strategies，viewer+ 只读）。
 * queryKey ['home-section-strategies']（全局、无 App scope —— 策略注册表是 server 全局集，不分 App）。
 * 端点返回全集（含 enabled=false 已下线行），**这里 filter 只暴露 enabled=true 给 BlockDetail 下拉**
 * （决策：保 3 策略简化 UI，下拉不列已下线策略；过滤放 hook 单一收口，下游无需重复 filter）。
 */
export function useHomeSectionStrategies(): {
  data: HomeSectionStrategyResponse[];
  isLoading: boolean;
  isError: boolean;
} {
  const q = useQuery({
    queryKey: ['home-section-strategies'],
    queryFn: async (): Promise<HomeSectionStrategyResponse[]> => {
      // @real — opsApi.listHomeSectionStrategies()（GET /api/v1/ops/home-section-strategies）
      const all = await opsApi.listHomeSectionStrategies();
      return all.filter((s) => s.enabled);
    },
  });
  // isError 单独暴露（codex#79 bot）：请求失败时 data=[] fallback 但 isLoading=false，下游不能把「加载失败」
  // 当「加载完确为空（0 enabled）」——否则 BlockDetail 会把所有栏目误判已下线、阻断 title-only 编辑。
  return {
    data: q.data ?? HOME_SECTION_STRATEGIES_FALLBACK,
    isLoading: q.isLoading,
    isError: q.isError,
  };
}

// ---------- FAQs ----------
export function useFaqs(
  scopeAppId = 'all',
  language = 'en',
): { data: Faq[]; isLoading: boolean; configSource: OpsConfigSource } {
  const q = useQuery({
    queryKey: ['faqs', scopeAppId, language],
    queryFn: async () => {
      const response = await opsApi.listFaqs(scopeAppId === 'all' ? null : scopeAppId, language);
      return { items: response.items.map(fromFaq), configSource: response.config_source };
    },
  });
  return {
    data: q.data?.items ?? FAQS_FALLBACK,
    isLoading: q.isLoading,
    configSource: q.data?.configSource ?? (scopeAppId === 'all' ? 'GLOBAL' : 'GLOBAL_INHERITED'),
  };
}

// ---------- FAQ 分类（faq_categories 注册表，FAQEditor 下拉 + OpsPage 分组表头源）----------
// 稳定空 fallback（checklist #1：禁每渲染 `?? []` 新建空数组）。
const FAQ_CATEGORIES_FALLBACK: FaqCategoryRow[] = [];
/**
 * useFaqCategories — FAQ 分类注册表（GET /api/v1/ops/faq-categories，plain array 非分页，viewer+ 只读）。
 * queryKey ['faq-categories']（global-only、无 App scope —— 分类表是 server 全局集）。
 * fromFaqCategory 塑成含展示 label 的行；FAQEditor 分类下拉候选 + OpsPage 分组表头标签据此渲染。
 */
export function useFaqCategories(): { data: FaqCategoryRow[]; isLoading: boolean } {
  const q = useQuery({
    queryKey: ['faq-categories'],
    queryFn: async (): Promise<FaqCategoryRow[]> => {
      // @real — opsApi.listFaqCategories()（GET /api/v1/ops/faq-categories）→ fromFaqCategory
      const cats = await opsApi.listFaqCategories();
      return cats.map(fromFaqCategory);
    },
  });
  return { data: q.data ?? FAQ_CATEGORIES_FALLBACK, isLoading: q.isLoading };
}

// ---------- Banner slides ----------
// 稳定空 fallback 常量（checklist #1：禁每渲染 `?? []` 新建空数组触发死循环）
const SLIDES_FALLBACK: BannerSlideResponse[] = [];
/**
 * useSlides — 某 Banner 位的轮播图列表（接真）。
 * queryKey ['banner-slides', bannerId]：与 use-ops-mutations 的 slide 写动作失效 key 对齐。
 * 返回原始 BannerSlideResponse[]（编辑/删除需 target_type / target_value / image_r2_key 原始字段，
 * 展示塑形由 BannerSlidesTab 经 ops-adapter.fromSlide 完成）。enabled=!!bannerId 防 banner 未选时空请求。
 */
export function useSlides(
  bannerId: string | null,
  language = 'en',
): {
  data: BannerSlideResponse[];
  isLoading: boolean;
} {
  const q = useQuery({
    queryKey: ['banner-slides', bannerId, language],
    enabled: !!bannerId,
    queryFn: async (): Promise<BannerSlideResponse[]> => {
      // @real — GET /api/v1/ops/banners/:id/slides?language=
      if (!bannerId) return SLIDES_FALLBACK;
      return opsApi.listSlides(bannerId, language);
    },
  });
  // 稳定空 fallback 常量（checklist #1：禁每渲染新建空数组）；enabled=false 时无视缓存返空（checklist #5）
  return { data: bannerId ? (q.data ?? SLIDES_FALLBACK) : SLIDES_FALLBACK, isLoading: q.isLoading };
}

// ---------- Product metadata（业务效果录入：vendor_product_id → coin/bonus）----------
const PRODUCT_METADATA_FALLBACK: ProductMetadataRow[] = [];

/** Web 独立商品目录；变更 invalidate 此 key，切 App/筛选/页码分别缓存。 */
export function useWebPaymentProducts(
  appId: string,
  query: {
    page: number;
    limit: number;
    search?: string;
    product_type?: 'CONSUMABLE' | 'SUBSCRIPTION';
  },
) {
  return useQuery({
    queryKey: ['web-payment-products', appId, query],
    queryFn: () => webPaymentProductsApi.list(appId, query),
    enabled: appId !== 'all',
    retry: false,
  });
}

/**
 * 某 App 的 product_metadata 业务效果列表（ADM-PAY 阶段1）。
 * 读：productMetadataApi.list(appId)（GET /api/v1/product-metadata?app_id=，后端不分页全量返回）→
 * adaptProductMetadata 塑成 ProductMetadataRow（snake→camel + 解析标题 + 保留非负整数串金额）。
 * 与 usePaymentPages（paywall 渠道配置）是两套不同数据：这里是 admin 录入的真实 coin/bonus 业务效果。
 * appId 进 queryKey 随顶栏/页内 App 切换重取；空 / 'all'（无具体 App）→ enabled=false 返稳定空集。
 */
export function useProductMetadata(appId: string): {
  data: ProductMetadataRow[];
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
} {
  const enabled = !!appId && appId !== 'all';
  const q = useQuery({
    queryKey: ['product-metadata', appId],
    enabled,
    queryFn: async (): Promise<ProductMetadataRow[]> => {
      // @real — productMetadataApi.list(appId)（GET /api/v1/product-metadata?app_id=）
      const rows = await productMetadataApi.list(appId);
      return rows.map(adaptProductMetadata);
    },
  });
  // 稳定空 fallback 常量（禁每渲染新建空数组）；enabled=false 时无视缓存返空
  return {
    data: enabled ? (q.data ?? PRODUCT_METADATA_FALLBACK) : PRODUCT_METADATA_FALLBACK,
    isLoading: q.isLoading,
    isSuccess: enabled && q.isSuccess,
    isError: q.isError,
  };
}

const ADAPTY_CREDENTIALS_FALLBACK: AdaptyCredentialResponse[] = [];
/**
 * Adapty 已配置 App 凭证列表（集成页，掩码）。// @real
 * 读：adaptyApi.listCredentials（GET /api/v1/integrations/adapty/credentials）。明文 Key 永不回显，只回 key_tail。
 */
export function useAdaptyCredentials(): {
  data: AdaptyCredentialResponse[];
  isLoading: boolean;
  isSuccess: boolean;
} {
  const q = useQuery({
    queryKey: ['adapty', 'credentials'],
    queryFn: () => adaptyApi.listCredentials(),
  });
  return {
    data: q.data ?? ADAPTY_CREDENTIALS_FALLBACK,
    isLoading: q.isLoading,
    isSuccess: q.isSuccess,
  };
}

const DEEPL_CREDENTIAL_FALLBACK: DeeplCredentialResponse = {
  configured: false,
  key_tail: null,
  status: null,
  last_verified_at: null,
  updated_at: null,
};
/**
 * 全局 DeepL 凭证视图（集成页，掩码）。// @real
 * 读：deeplApi.getCredential（GET /api/v1/integrations/deepl/credential）。明文 Key 永不回显，只回 configured + key_tail。
 */
export function useDeeplCredential(): {
  data: DeeplCredentialResponse;
  isLoading: boolean;
  isSuccess: boolean;
} {
  const q = useQuery({
    queryKey: ['integrations', 'deepl'],
    queryFn: () => deeplApi.getCredential(),
  });
  return {
    data: q.data ?? DEEPL_CREDENTIAL_FALLBACK,
    isLoading: q.isLoading,
    isSuccess: q.isSuccess,
  };
}

const STRIPE_CHECKOUT_CONFIGS_FALLBACK: StripeCheckoutConfigResponse[] = [];
/**
 * Stripe direct Checkout App 配置列表（集成页 / Stripe 配置弹窗）。// @real
 * 读：stripeCheckoutApi.listConfigs（GET /api/v1/integrations/stripe/checkout-configs）。
 * 响应**不含**任何 secret（key / tail / hash / 长度）与内部 mutation_epoch（A8 §9.1）。
 *
 * 端点是 `requireRole('root','admin')`（`routes/v1/integrations/stripe/index.ts`），**operator 恒
 * 422 + `FORBIDDEN_OPERATION`** —— admin-api 的角色拒绝走 `errors.forbidden()`
 * （`packages/server/src/lib/errors.ts`，写死 `httpStatus: 422`），而 `middleware/error-handler.ts`
 * 的返回类型闭集是 `401 | 422 | 500 | 503`：**HTTP 403 在本仓不可能出现**。
 * 故单独暴露 `isForbidden`：把「无权限」与「接口故障」压成同一个 isError，会让 operator 在只读展示
 * 场景里恒看到「加载失败」，无从分辨该找谁——排障方向直接被带偏。
 */
export function useStripeCheckoutConfigs(): {
  data: StripeCheckoutConfigResponse[];
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  isForbidden: boolean;
} {
  const q = useQuery({
    queryKey: ['integrations', 'stripe', 'checkout-configs'],
    queryFn: () => stripeCheckoutApi.listConfigs(),
  });
  return {
    data: q.data ?? STRIPE_CHECKOUT_CONFIGS_FALLBACK,
    isLoading: q.isLoading,
    isSuccess: q.isSuccess,
    isError: q.isError,
    // 判 code 而非 httpStatus：本仓的角色拒绝是 422 + FORBIDDEN_OPERATION，只判 403 这一档在生产
    // 恒不可达（operator 看到的会是「加载失败」）。403 保留作 belt-and-braces：本仓产不出，
    // 但反代 / 边缘（CF WAF）仍可能在到达 Worker 前直接回它。
    isForbidden:
      q.error instanceof AdminApiError &&
      (q.error.code === SYSTEM_ERROR_CODES.FORBIDDEN_OPERATION || q.error.httpStatus === 403),
  };
}

/**
 * Adapty catalog（placement + vendor_product_id 候选）按 App。// @real
 * 读：adaptyApi.catalog（GET /api/v1/payments/adapty/catalog?app_id=）。未配置 App 后端返
 * PAY_ADAPTY_NOT_CONFIGURED（422）/ 上游故障 503 → 页面读 isError 退到「手输兜底 + 引导去集成页」。
 * appId 空 / 'all' → enabled=false；retry=false（未配置/上游错误不刷调用）。返原始 useQuery 供页面读 isError/error。
 */
export function useAdaptyCatalog(appId: string | undefined) {
  const enabled = !!appId && appId !== 'all';
  return useQuery({
    queryKey: ['adapty', 'catalog', appId],
    enabled,
    retry: false,
    queryFn: () => adaptyApi.catalog(appId as string),
  });
}

// ---------- 支付通道策略（ADM-PAY-S008，A8 §7.8）----------
// 稳定空 fallback：每渲染复用同一引用，避免 `?? []` 每渲染新建空数组触发消费方 effect 死循环
const PAYMENT_METHOD_POLICIES_FALLBACK: PaymentMethodPolicyResponse[] = [];

export const paymentMethodPolicyQueryKeys = {
  all: ['payment-method-policies'] as const,
  byApp: (appId: string) => ['payment-method-policies', appId] as const,
};

/**
 * 某 App 的通道策略组列表（GET /api/v1/payment-method-policies?app_id=，A8 §7.8.1）。
 * per-App 策略数有界 → 后端不分页全量返回：全局组（`region_code=null`）恒首位，其后各 Region 覆盖组
 * 按 region_code 升序，组内成员按 sort_order 升序。
 *
 * **DTO 直消费、不过 adapter**：三态（组不存在 / 组存在且零成员 / 有序成员）全靠 wire 形状本身表达，
 * 任何「归一空组」「按通道 flatten」的塑形都会抹掉「零成员组」与「无该组」的区别，而这两者 serving
 * 行为相反（§7.8.0）。appId 进 queryKey 随顶栏 / 页内 App 切换重取；空 / 'all' → enabled=false 返稳定空集。
 */
export function usePaymentMethodPolicies(appId: string): {
  data: PaymentMethodPolicyResponse[];
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
} {
  const enabled = !!appId && appId !== 'all';
  const q = useQuery({
    queryKey: paymentMethodPolicyQueryKeys.byApp(appId),
    enabled,
    // @real — paymentMethodPoliciesApi.list（GET /api/v1/payment-method-policies）
    queryFn: () => paymentMethodPoliciesApi.list(appId),
  });
  return {
    data: enabled ? (q.data ?? PAYMENT_METHOD_POLICIES_FALLBACK) : PAYMENT_METHOD_POLICIES_FALLBACK,
    isLoading: q.isLoading,
    isError: q.isError,
    // `enabled &&`：查询被禁用时返的是稳定空 fallback、不是服务端的空集，两者后果相反
    //（见 useAppPaymentMethods 文件注释；消费方靠 isSuccess 区分「成功拿到空集」与「什么都没拿到」）
    isSuccess: enabled && q.isSuccess,
  };
}

// ---------- 支付方式图标设置（ADM-APP-S013，A8 §7.8.5~§7.8.7）----------
// 稳定空 fallback：同 policies，避免 `?? []` 每渲染新建引用
const PAYMENT_METHOD_SETTINGS_FALLBACK: PaymentMethodSettingResponse[] = [];

export const paymentMethodSettingQueryKeys = {
  all: ['payment-method-settings'] as const,
  byApp: (appId: string) => ['payment-method-settings', appId] as const,
};

/**
 * 某 App 的支付方式图标设置（GET /api/v1/payment-method-settings?app_id=，A8 §7.8.5）。
 *
 * **「未配置图标」有两种物理形态**（§7.8.2 图标前置校验同口径）：该支付方式的行**整行缺失**，
 * 或行在但 `icon_asset_id` 为 `null`。消费方判「能不能进策略成员」时两者等价，故不要用
 * 「行存在」当作已配置 —— 按 `icon_asset_id` 非空判。
 *
 * DTO 直消费、不过 adapter：形状已是 per 支付方式的三元组（`payment_method` / `icon_asset_id` /
 * 派生 `icon_url`），塑形只会丢掉上面那条区分。
 */
export function usePaymentMethodSettings(appId: string): {
  data: PaymentMethodSettingResponse[];
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
} {
  const enabled = !!appId && appId !== 'all';
  const q = useQuery({
    queryKey: paymentMethodSettingQueryKeys.byApp(appId),
    enabled,
    // @real — paymentMethodSettingsApi.list（GET /api/v1/payment-method-settings）
    queryFn: () => paymentMethodSettingsApi.list(appId),
  });
  return {
    data: enabled ? (q.data ?? PAYMENT_METHOD_SETTINGS_FALLBACK) : PAYMENT_METHOD_SETTINGS_FALLBACK,
    isLoading: q.isLoading,
    isError: q.isError,
    // 同 usePaymentMethodPolicies：禁用态返的是稳定空 fallback、不是服务端空集
    isSuccess: enabled && q.isSuccess,
  };
}

// ---------- 可选支付方式 + Stripe 同步（ADM-PAY-S013，A8 §7.8.8 / §7.8.9）----------
export const paymentMethodOptionQueryKeys = {
  all: ['payment-method-options'] as const,
  byApp: (appId: string) => ['payment-method-options', appId] as const,
};

/**
 * 某 App 的可选支付方式（GET /api/v1/payment-method-options?app_id=，A8 §7.8.8）：平台适用的固定子集
 * （§3 映射表行序）+ 全部 Stripe 同步行（`payment_method` 升序）。策略成员候选与图标入口只取这里的值。
 *
 * DTO 直消费、不过 adapter：`is_available` 的三态（`null` = 固定子集不受约束 / `false` = 同步但当前不下发 /
 * `true`）本身就是 UI 要讲的事实，塑形成布尔会把固定子集误标成「不可用」。
 * 未就绪时 `data` 为 `null`（不给稳定空壳）：消费方须区分「还没拿到」与「只有固定子集」。
 */
export function usePaymentMethodOptions(appId: string): {
  data: PaymentMethodOptionsResponse | null;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  /** 读取失败后的重试入口（viewer 没有同步按钮，只能靠它恢复）*/
  refetch: () => void;
} {
  const enabled = !!appId && appId !== 'all';
  const q = useQuery({
    queryKey: paymentMethodOptionQueryKeys.byApp(appId),
    enabled,
    // @real — paymentMethodOptionsApi.list（GET /api/v1/payment-method-options）
    queryFn: () => paymentMethodOptionsApi.list(appId),
  });
  return {
    data: enabled ? (q.data ?? null) : null,
    isLoading: q.isLoading,
    isError: q.isError,
    isSuccess: enabled && q.isSuccess,
    refetch: () => void q.refetch(),
  };
}

const PAYMENT_OFFERS_FALLBACK: OfferRow[] = [];
/** offer 列表单页上限（admin offset 上限 200）；total > 200 时 hasMore=true，页内显示提示（翻页 UI = follow-up，记 plan NOT-in-scope）*/
const OFFERS_PAGE_LIMIT = 200;

/**
 * 某 App 的运营支付活动 offer 列表（ADM-PAY-S005）。
 * 读：paymentOffersApi.list（GET /api/v1/payment-offers?app_id=&is_active=&page=&limit=，offset 分页壳）→
 * adaptOffer 塑成 OfferRow（snake→camel）。本 MVP 取首页（limit 200），返 total/hasMore 供页内提示是否还有更多。
 * appId 进 queryKey 随顶栏/页内 App 切换重取；空 / 'all'（无具体 App）→ enabled=false 返稳定空集。
 */
export function usePaymentOffers(
  appId: string,
  filters?: { isActive?: boolean },
): { data: OfferRow[]; isLoading: boolean; total: number; hasMore: boolean } {
  const enabled = !!appId && appId !== 'all';
  const q = useQuery({
    queryKey: ['payment-offers', appId, filters?.isActive ?? null],
    enabled,
    queryFn: async () => {
      // @real — paymentOffersApi.list（GET /api/v1/payment-offers，offset 分页）
      const page = await paymentOffersApi.list({
        app_id: appId,
        is_active: filters?.isActive,
        page: 1,
        limit: OFFERS_PAGE_LIMIT,
      });
      return { ...page, data: page.data.map(adaptOffer) };
    },
  });
  return {
    data: enabled ? (q.data?.data ?? PAYMENT_OFFERS_FALLBACK) : PAYMENT_OFFERS_FALLBACK,
    isLoading: q.isLoading,
    total: q.data?.total ?? 0,
    hasMore: q.data?.hasMore ?? false,
  };
}

// 稳定空 fallback（避免每渲染新建空数组触发镜像 effect 死循环）
const PAYMENT_PAGES_FALLBACK: PaywallPageItem[] = [];

/**
 * 某 App 的支付页面列表（ADM-PAY-S001~S003）。
 * 读：paymentPagesApi.list(appId) 取页面 + 每页 listPaywalls(id) 取渠道配置（N+1）→ adaptPaymentPages
 * 塑成 PaywallPageItem（channels 由 paywalls 派生；products / by 真 API 无 → adapter 默认，由 Adapty 承接）。
 * appId 进 queryKey 随顶栏/页内 App 切换重取；空 appId（无真实 App）→ enabled=false 返稳定空集。
 */
export function usePaymentPages(appId: string): {
  data: PaywallPageItem[];
  isLoading: boolean;
  isSuccess: boolean;
} {
  const enabled = !!appId && appId !== 'all';
  const q = useQuery({
    queryKey: ['payment-pages', appId],
    enabled,
    queryFn: async (): Promise<PaywallPageItem[]> => {
      // @real — paymentPagesApi.list(appId)（GET /api/v1/payment-pages?app_id=）
      //   + 每页 listPaywalls(id)（GET /api/v1/payment-pages/:id/paywalls）→ adaptPaymentPages
      const pages = await paymentPagesApi.list(appId);
      const perPage = await Promise.allSettled(
        pages.map((p) => paymentPagesApi.listPaywalls(p.id)),
      );
      // F5：listPaywalls 失败**不**静默当空配置（瞬时 401/500 → channels 显示为空 → 运营 publish 覆盖残缺数据）。
      // 仅显式 fulfilled（含成功返空数组）才传 paywalls；rejected → paywallsError=true，页面禁该页 placement 写/publish。
      const paywallsByPageId: Record<string, PaywallConfigResponse[]> = {};
      const errorByPageId: Record<string, boolean> = {};
      pages.forEach((p, i) => {
        const r = perPage[i];
        if (r && r.status === 'fulfilled') {
          paywallsByPageId[p.id] = r.value;
        } else {
          errorByPageId[p.id] = true;
        }
      });
      return adaptPaymentPages(pages, (p) =>
        errorByPageId[p.id] ? { paywallsError: true } : { paywalls: paywallsByPageId[p.id] ?? [] },
      );
    },
  });
  // enabled=false 无视缓存返稳定空集（防降权/切 App 读上一 App 残留）
  return {
    data: enabled ? (q.data ?? PAYMENT_PAGES_FALLBACK) : PAYMENT_PAGES_FALLBACK,
    isLoading: q.isLoading,
    isSuccess: enabled && q.isSuccess,
  };
}

// ---------- Finance（分维度统计 + 产品销量，原币多币种）----------
/** 最小币种单位（分，string）→ 主单位（number）。非法 / 空值落 0。 */
function minorToMajor(minor: string, currency: string): number {
  return currencyMinorToMajorNumber(minor, currency);
}

/** 单币种的分维度 + 产品销量视图数据 */
export type FinanceCurrencyData = {
  byPlatform: FinanceRow[];
  byChannel: FinanceRow[];
  productSales: ProductSales[];
};
/** 多币种 finance 数据：currencies 全集（升序）+ 按币种索引（原币不归一，张培 #119）*/
export type FinanceMultiCurrencyData = {
  currencies: string[];
  byCurrency: Record<string, FinanceCurrencyData>;
};

/** 单币种收入聚合（incomeStats 真值 minor→主单位换算后）。 */
export type FinanceIncomeRow = {
  chargeOrders: number;
  chargeUsers: number;
  avgChargeCountPerUser: number;
  totalRevenue: number;
  avgOrderValue: number;
};
/** 多币种收入统计：currencies 升序 + 按币种索引 + scope/appId 回显（ADM-FIN-S001）。 */
export type FinanceIncomeData = {
  scope: 'all' | 'app';
  appId: string | null;
  currencies: string[];
  byCurrency: Record<string, FinanceIncomeRow>;
};
/** 单币种退款聚合（refundStats 真值）。 */
export type FinanceRefundRow = {
  refundUsers: number;
  refundCount: number;
  totalRefund: number;
};
/** 多币种退款统计：顶层 passiveRefundShare（占比"1"/"0"）+ 按币种索引（ADM-FIN-S002）。 */
export type FinanceRefundData = {
  scope: 'all' | 'app';
  appId: string | null;
  /** 被动退款占全部退款比（本表全被动 → 1/0，与币种无关；≠ S005 退款率）。null = 加载中。 */
  passiveRefundShare: number | null;
  currencies: string[];
  byCurrency: Record<string, FinanceRefundRow>;
};

/** ?app_id 注入口径：当前 App scope（'all' → 不传 = 全量汇总，具体 App → 传 UUID）。 */
function appIdQuery(appId: string): string | undefined {
  return appId === 'all' ? undefined : appId;
}

const FINANCE_INCOME_FALLBACK: FinanceIncomeData = {
  scope: 'all',
  appId: null,
  currencies: [],
  byCurrency: {},
};

/** ADM-FIN-S001 收入统计（原币分币种，随 App scope 注入 app_id）。 */
export function useIncomeStats(): {
  data: FinanceIncomeData;
  isLoading: boolean;
} {
  const appId = useAppScope((s) => s.appId);
  const time = useFinanceTimeStore((s) => s.time);
  const q = useQuery({
    queryKey: ['finance-income', appId, time.period, time.from, time.to],
    queryFn: async (): Promise<FinanceIncomeData> => {
      // @real — financeApi.incomeStats(app_id, 时间窗)（GET /api/v1/finance/stats/income）；by_currency 分组、minor→主单位
      const dto = await financeApi.incomeStats(appIdQuery(appId), time);
      const byCurrency: Record<string, FinanceIncomeRow> = {};
      for (const g of dto.by_currency) {
        byCurrency[g.currency] = {
          chargeOrders: g.charge_orders,
          chargeUsers: g.charge_users,
          avgChargeCountPerUser: Number(g.avg_charge_count_per_user) || 0,
          totalRevenue: minorToMajor(g.total_revenue_minor_units, g.currency),
          avgOrderValue: minorToMajor(g.avg_order_value_minor_units, g.currency),
        };
      }
      return {
        scope: dto.scope,
        appId: dto.app_id,
        currencies: dto.by_currency.map((g) => g.currency).sort(),
        byCurrency,
      };
    },
  });
  return { data: q.data ?? FINANCE_INCOME_FALLBACK, isLoading: q.isLoading };
}

const FINANCE_REFUND_FALLBACK: FinanceRefundData = {
  scope: 'all',
  appId: null,
  passiveRefundShare: null,
  currencies: [],
  byCurrency: {},
};

/** ADM-FIN-S002 退款统计（原币分币种 + 顶层占比，随 App scope 注入 app_id）。 */
export function useRefundStats(): {
  data: FinanceRefundData;
  isLoading: boolean;
} {
  const appId = useAppScope((s) => s.appId);
  const time = useFinanceTimeStore((s) => s.time);
  const q = useQuery({
    queryKey: ['finance-refund-stats', appId, time.period, time.from, time.to],
    queryFn: async (): Promise<FinanceRefundData> => {
      // @real — financeApi.refundStats(app_id, 时间窗)（GET /api/v1/finance/stats/refund）
      const dto = await financeApi.refundStats(appIdQuery(appId), time);
      const byCurrency: Record<string, FinanceRefundRow> = {};
      for (const g of dto.by_currency) {
        byCurrency[g.currency] = {
          refundUsers: g.refund_users,
          refundCount: g.refund_count,
          totalRefund: minorToMajor(g.total_refund_minor_units, g.currency),
        };
      }
      return {
        scope: dto.scope,
        appId: dto.app_id,
        passiveRefundShare: Number(dto.passive_refund_share) || 0,
        currencies: dto.by_currency.map((g) => g.currency).sort(),
        byCurrency,
      };
    },
  });
  return { data: q.data ?? FINANCE_REFUND_FALLBACK, isLoading: q.isLoading };
}

/** ADM-FIN 退款率逐日趋势（随 App scope + 页头时间窗）：返回逐日点供 AreaChart。 */
export function useRefundTrend(): {
  data: FinanceRefundTrendPoint[];
  isLoading: boolean;
} {
  const appId = useAppScope((s) => s.appId);
  const time = useFinanceTimeStore((s) => s.time);
  const q = useQuery({
    queryKey: ['finance-refund-trend', appId, time.period, time.from, time.to],
    queryFn: async (): Promise<FinanceRefundTrendPoint[]> => {
      const dto = await financeApi.refundTrend(appIdQuery(appId), time);
      return dto.points;
    },
  });
  return { data: q.data ?? REFUND_TREND_FALLBACK, isLoading: q.isLoading };
}
const REFUND_TREND_FALLBACK: FinanceRefundTrendPoint[] = [];

/**
 * BONUS 发放月度 KPI（ADM-FIN-S004 配套，平台级）。
 * - amount 为 BONUS **数量**（非货币 minor units）→ 不做 minorToMajor，Number 化后原值展示。
 * - current/previous 两段透传，环比 delta 由消费方（GrantBonusForm）按 (cur−prev)/prev 真实计算。
 * - 平台级（bonus_grants 无 app_id 列）→ 不随 App scope 注入 app_id；queryKey 无 appId 维度。
 * - data=null 表示加载中（无 fallback 假数据）；count=0 时 avg 由服务端返 '0'。
 * - isError：查询失败（重试后仍失败）→ 消费方据此显「—」而非 ?? 0（否则故障被伪装成「本月无发放」，codex PR#77）。
 * - totalAmount/avgPerGrant 保留**整数 string**（不 Number 化）：服务端用 bigint 防精度丢失，>2^53 的总额
 *   Number() 会四舍五入（service test 覆盖 9007199254740993）→ 消费方按 BigInt 渲染、Number 仅用于环比 ratio（codex PR#77）。
 */
export type BonusStatsView = {
  current: { totalAmount: string; grantCount: number; avgPerGrant: string };
  previous: { totalAmount: string; grantCount: number; avgPerGrant: string };
};

export function useBonusStats(): {
  data: BonusStatsView | null;
  isLoading: boolean;
  isError: boolean;
} {
  const q = useQuery({
    queryKey: ['finance-bonus-stats'],
    queryFn: async (): Promise<BonusStatsView> => {
      // @real — financeApi.bonusStats()（GET /api/v1/finance/stats/bonus）；amount 为 BONUS 数量、非货币
      const dto = await financeApi.bonusStats();
      const toRow = (s: { total_amount: string; grant_count: number; avg_per_grant: string }) => ({
        // 整数 string 透传保精度（消费方 BigInt 渲染）；grant_count 是 count(*)::int、number 安全
        totalAmount: s.total_amount,
        grantCount: s.grant_count,
        avgPerGrant: s.avg_per_grant,
      });
      return { current: toRow(dto.current), previous: toRow(dto.previous) };
    },
  });
  return { data: q.data ?? null, isLoading: q.isLoading, isError: q.isError };
}

/**
 * App 营收排行单行（incomeStats per-App fan-out 真值；按选中币种取该 App 收入）。
 * unavailable=true：该 App incomeStats 超时 / 500 → 收入未知（不可当 0 计入占比分母）。
 */
export type FinanceAppRevenueRow = {
  id: string;
  name: string;
  byCurrency: Record<string, number>;
  unavailable: boolean;
};

const FINANCE_APP_REVENUE_FALLBACK: FinanceAppRevenueRow[] = [];

/**
 * App 营收排行（ADM-FIN-S001 按 App 维度）：fan-out incomeStats(appId) 逐 App 取真实原币收入。
 * income-stats 端点本身只返 by_currency 汇总（无 per-app 拆分）→ 逐 App 调一次，按币种聚合该 App 收入。
 * 替代旧实现的硬编码 `a.revenue / 1142380` 魔法占比（真 App revenue 聚合 admin 端不另存，故 N+1）。
 *
 * F1（跨 App 泄漏，adversarial r2）：营收排行是**全平台视图专属** widget——跨 App 横向对比只在 'all'
 * scope 下有意义。**queryKey 含 appId**（不再常量 key，否则 all→单App 复用跨 App 缓存结果）；当 scope
 * 收窄到具体 App（appId !== 'all'）时返**空集**（enabled=false 不再 fan-out 其它 App incomeStats），
 * 页面据此显「排行仅全平台视图可见」空态——绝不在被 scope 的页头下请求 / 渲染其它 App 的收入。
 *
 * F4（部分失败静默降级）：某 App 超时 / 500 不再被 Promise.allSettled 丢掉 → 标 unavailable=true 保留该行，
 * 页面据此对失败行显「数据异常」且**不用残缺集重算 100% 占比**（避免部分数据伪装完整）。
 */
export function useFinanceIncomeByApp(): {
  data: FinanceAppRevenueRow[];
  isLoading: boolean;
} {
  const appId = useAppScope((s) => s.appId);
  const time = useFinanceTimeStore((s) => s.time);
  // 排行仅全平台视图（'all'）可见：单 App scope 不 fan-out 其它 App incomeStats（防跨 App 泄漏 F1）
  const isAllScope = appId === 'all';
  const q = useQuery({
    queryKey: ['finance-income-by-app', appId, time.period, time.from, time.to],
    enabled: isAllScope,
    queryFn: async (): Promise<FinanceAppRevenueRow[]> => {
      // @real — listApps() + 逐 App financeApi.incomeStats(app.id, 时间窗)，按币种取该 App 原币总收入
      const apps = await listApps();
      const settled = await Promise.allSettled(
        apps.map(async (a) => {
          const dto = await financeApi.incomeStats(a.id, time);
          const byCurrency: Record<string, number> = {};
          for (const g of dto.by_currency) {
            byCurrency[g.currency] = minorToMajor(g.total_revenue_minor_units, g.currency);
          }
          return { id: a.id, name: a.name, byCurrency };
        }),
      );
      // 失败行也保留（标 unavailable），不静默丢；apps 与 settled 索引一一对应
      return apps.map((a, i) => {
        const r = settled[i];
        if (r && r.status === 'fulfilled') {
          return { ...r.value, unavailable: false };
        }
        return { id: a.id, name: a.name, byCurrency: {}, unavailable: true };
      });
    },
  });
  // enabled=false（单 App scope）→ 返模块级稳定空集（FALLBACK），不返其它 App 缓存（checklist #5）
  return { data: q.data ?? FINANCE_APP_REVENUE_FALLBACK, isLoading: q.isLoading };
}

export function useFinance(): {
  data: FinanceMultiCurrencyData;
  isLoading: boolean;
} {
  const appId = useAppScope((s) => s.appId);
  const time = useFinanceTimeStore((s) => s.time);
  const q = useQuery({
    queryKey: ['finance', appId, time.period, time.from, time.to],
    queryFn: async (): Promise<FinanceMultiCurrencyData> => {
      // @real — 三端点并行（GET /finance/stats/by-platform + /by-channel + /product-sales），多币种 by_currency
      // → finance-adapter 按币种组塑形（minor→主单位、ratio→百分数）。原币不归一 → 页面用币种选择器切换。
      // 随 App scope 注入 app_id（'all' → 全量汇总，具体 App → 该 App 维度）+ 页头时间窗 period/from/to。
      const appQuery = appIdQuery(appId);
      const tp = {
        ...(time.period ? { period: time.period } : {}),
        ...(time.from ? { from: time.from } : {}),
        ...(time.to ? { to: time.to } : {}),
      };
      const [byPlatform, byChannel, productSales] = await Promise.all([
        apiRequest<FinanceDimensionStatsResponse>({
          method: 'GET',
          path: '/api/v1/finance/stats/by-platform',
          query: { ...(appQuery ? { app_id: appQuery } : {}), ...tp },
        }),
        apiRequest<FinanceDimensionStatsResponse>({
          method: 'GET',
          path: '/api/v1/finance/stats/by-channel',
          query: { ...(appQuery ? { app_id: appQuery } : {}), ...tp },
        }),
        apiRequest<FinanceProductSalesResponse>({
          method: 'GET',
          path: '/api/v1/finance/product-sales',
          query: { ...(appQuery ? { app_id: appQuery } : {}), ...tp },
        }),
      ]);
      // 币种全集（三端点 union，升序稳定）
      const currencies = [
        ...new Set([
          ...byPlatform.by_currency.map((g) => g.currency),
          ...byChannel.by_currency.map((g) => g.currency),
          ...productSales.by_currency.map((g) => g.currency),
        ]),
      ].sort();
      const byCurrency: Record<string, FinanceCurrencyData> = {};
      for (const cur of currencies) {
        const p = byPlatform.by_currency.find((g) => g.currency === cur);
        const ch = byChannel.by_currency.find((g) => g.currency === cur);
        const pr = productSales.by_currency.find((g) => g.currency === cur);
        byCurrency[cur] = {
          byPlatform: p ? adaptFinanceDimensionRows(p) : [],
          byChannel: ch ? adaptFinanceDimensionRows(ch) : [],
          productSales: pr ? adaptFinanceProductSales(pr) : [],
        };
      }
      return { currencies, byCurrency };
    },
  });
  return { data: q.data ?? { currencies: [], byCurrency: {} }, isLoading: q.isLoading };
}

// ---------- Risk rules ----------
// loading / 失败兜底：稳定模块常量（同一引用）——避免每次渲染 `?? []` 新建空数组，
// 触发消费方 useEffect（RiskPolicy 的 setRules(riskRules)）依赖每渲染变化 → maximum update
// depth 死循环（同 SYS_CONFIG_FALLBACK 修法，codex adversarial #53 r2）。
const RISK_RULES_FALLBACK: RiskRuleRow[] = [];

export function useRiskRules(): {
  data: RiskRuleRow[];
  isLoading: boolean;
  isError: boolean;
  isServiceUpdating: boolean;
  isFetching: boolean;
  refetch: () => void;
} {
  const appId = useAppScope((state) => state.appId);
  const q = useQuery({
    queryKey: ['risk-rules', appId],
    retry: false,
    queryFn: async (): Promise<RiskRuleRow[]> => {
      // @real — 全局v1 / App独立v2 → risk-adapter；404只报更新中，不用全局结果代替App。
      const rules = await apiRequest<(RiskRuleResponse | RiskRuleV1Response)[]>({
        method: 'GET',
        path: appId === 'all' ? '/api/v1/risk-rules' : `/api/v2/apps/${appId}/risk-rules`,
      });
      return adaptRiskRules(rules ?? []);
    },
  });
  return {
    data: q.data ?? RISK_RULES_FALLBACK,
    isLoading: q.isLoading,
    isFetching: q.isFetching,
    isError: q.isError,
    isServiceUpdating:
      appId !== 'all' && q.error instanceof AdminApiError && q.error.httpStatus === 404,
    refetch: () => {
      void q.refetch();
    },
  };
}

// ---------- Device whitelist（设备白名单，ADM-USR-S005，root 专属）----------
/**
 * 设备白名单页面行：白名单行 UUID（DELETE /device-whitelist/:id 用）+ 设备 ID + 备注 + 添加时间。
 * 真 DTO（DeviceWhitelistResponse）无「归属用户 / 操作员」字段（契约无 device↔user 关联），
 * 页面以「—」呈现，不编造。
 */
export type DeviceWhitelistRow = {
  id: string;
  appId: string;
  deviceId: string;
  reason: string | null;
  createdAt: string;
};

/**
 * 设备白名单列表（GET /api/v1/device-whitelist，张培 PR332 迁顶层 + per-App key）。RBAC viewer+（读放宽，
 * 不再 root 机密）；可选 app_id 过滤（顶栏 app scope，'all' 传 undefined = 全部 App）。
 */
// loading / 失败兜底：稳定模块常量（同一引用），避免 `?? []` 每渲染新建触发消费方 useEffect 死循环。
const DEVICE_WHITELIST_FALLBACK: DeviceWhitelistRow[] = [];

export function useDeviceWhitelist(
  appId?: string,
  enabled = true,
): {
  data: DeviceWhitelistRow[];
  isLoading: boolean;
} {
  const q = useQuery({
    queryKey: ['device-whitelist', appId ?? 'all'],
    enabled,
    queryFn: async (): Promise<DeviceWhitelistRow[]> => {
      // @real — usersApi.listDeviceWhitelist(appId)（GET /api/v1/device-whitelist?app_id，offset 壳）→ 页面行
      const res = await usersApi.listDeviceWhitelist(appId);
      return (res.data ?? []).map((r) => ({
        id: r.id,
        appId: r.app_id,
        deviceId: r.device_id,
        reason: r.reason,
        createdAt: r.created_at,
      }));
    },
  });
  return {
    data: enabled ? (q.data ?? DEVICE_WHITELIST_FALLBACK) : DEVICE_WHITELIST_FALLBACK,
    isLoading: enabled ? q.isLoading : false,
  };
}

// ---------- Dashboard（KPI 标量 + 时间序列 + App 营收 + 热门书籍 + 告警）----------
/** loading / 失败兜底：KPI 全 0、各列表空数组，避免页面解构 undefined 崩溃 */
const DASHBOARD_FALLBACK: DashboardViewData = {
  currency: 'USD',
  kpi: {
    totalRevenue: { value: 0, deltaPct: 0 },
    payingUsers: { value: 0, deltaPct: 0 },
    dau: { value: 0, deltaPct: 0, pending: true },
    arppu: { value: null, currency: 'USD', deltaPct: 0 },
    passiveRefundRate: { value: 0, deltaPp: 0 },
  },
  behavior: { pending: true, missingAppIds: [] },
  revenueByApp: [],
  topBooks: [],
  alerts: { pendingCount: 0, items: [] },
  revenue30d: [],
  users30d: [],
  dau30d: [],
  refund30d: [],
  revenueBreakdown: [],
  retention: [],
  funnel: [],
  funnelOverallRate: null,
};

/** useDashboard 参数：时间窗 + App scope（均显式传，不从 store 读，见下方 Why）*/
export type UseDashboardOptions = {
  /** 时间窗（24h/7d/30d/90d 闭集），缺省 30d */
  range?: DashboardRange;
  /** App scope：'all' 聚合 | UUID 单 App，缺省 'all'（**不从全局 AppSwitcher store 读**）*/
  appScope?: string;
};

/**
 * dashboard overview 取数（ADM-DASH-S001~S009）。
 *
 * 参数化（显式传入，**不从全局 store 读**——adversarial F1）：
 * - appScope / range 均由调用方显式传，缺省 `{ range: '30d', appScope: 'all' }`。
 *   **Why 不在 hook 内部读 useAppScope().appId**：dashboard overview 是多页共享的聚合数据源
 *   （UsersPage / AppsPage DetailOverview / FinancePage / BooksPage / PaywallPage 都只取其中
 *   revenue30d/refund30d/revenueBreakdown 等通用时序切片，与「当前选中哪个 App」无关）。若 hook
 *   无条件读全局 AppSwitcher 的 appId 进 queryKey，这些无参调用方会被顶栏切换拖着跟随重取，且把
 *   单 App overview 数据混进本与 App 无关的页面（数据污染 + 多余 refetch）。
 * - **只有 DashboardPage 显式传 `{ range, appScope: 顶栏 appId }`**——它是 App 维度一等公民页，
 *   切 App 应重取；契约 §1.2 scope=app 时 revenue_by_app 返 []（页面渲染占位卡）。
 * - appScope / range 进 queryKey `['dashboard', appScope, range]`——切维度重取；无参调用方恒为
 *   `['dashboard', 'all', '30d']`（与顶栏解耦）。刷新按钮 invalidateQueries(['dashboard']) 命中
 *   前缀重取所有维度的 dashboard query。
 */
export function useDashboard({ range = '30d', appScope = 'all' }: UseDashboardOptions = {}): {
  data: DashboardViewData;
  isLoading: boolean;
  /** 取数失败（初始 503/401/422 或刷新失败且无上次成功数据）；页面据此渲染错误态而非零数据兜底 */
  isError: boolean;
  /** 失败时是否仍有上次成功数据可展示（refetch 失败但 cache 有旧值）；用于「刷新失败、保留旧值」标注 */
  hasData: boolean;
  /** 触发重取（错误态「重试」按钮用）*/
  refetch: () => void;
} {
  const q = useQuery({
    queryKey: ['dashboard', appScope, range],
    queryFn: async (): Promise<DashboardViewData> => {
      // @real — GET /api/v1/dashboard/overview?app_scope=<all|uuid>&range=<range> → adaptDashboardOverview
      // （minor→major number、行为埋点占位字段 dau/retention/funnel 降级空数组 / pending 标记，
      //   页面空态渲染；realtime ADM-DASH-S007 本轮不实现，overview 已不含该块）。
      const dto = await apiRequest<DashboardOverviewResponse>({
        method: 'GET',
        path: '/api/v1/dashboard/overview',
        query: { app_scope: appScope, range },
      });
      return adaptDashboardOverview(dto);
    },
  });
  // adversarial F2：q.data 缺失时返稳定 DASHBOARD_FALLBACK（避免页面解构 undefined），但必须同时
  // 暴露 isError——否则初始 503/401/422 时页面把零 KPI / 空图当真实数据渲染（违反「数字来自真 API、
  // 无合成数据」，让后端故障 / 无效 scope 看起来像真实零活动）。页面据 isError + hasData 分流：
  // 无数据失败 → 错误态；有上次成功数据的刷新失败 → 保留旧值 + 标注「刷新失败」。
  return {
    data: q.data ?? DASHBOARD_FALLBACK,
    isLoading: q.isLoading,
    isError: q.isError,
    hasData: q.data !== undefined,
    refetch: () => {
      void q.refetch();
    },
  };
}

// ---------- 数据中台 · 内容消费（ADM-MTR-S003）----------

const METRICS_ENGAGEMENT_FALLBACK: MetricsEngagementViewData = {
  computedAt: null,
  window: { from: '', to: '' },
  readTrend: { points: [] },
  retention: {
    pending: true,
    milestones: [1, 3, 7, 14, 30],
    cohorts: [],
    totalCohortSize: 0,
  },
  contentTypes: { pending: false, partial: true, rows: [] },
};

/** useMetricsEngagement 参数：时间窗 + App scope（内容消费页顶栏 appId 显式传入） */
export type UseMetricsEngagementOptions = {
  range?: MetricsRange;
  appScope?: string;
};

/**
 * 数据中台 L2-B 内容消费取数（ADM-MTR-S003）。
 *
 * appScope / range 进 queryKey `['metrics', 'engagement', appScope, range]`——切维度重取；
 * 服务端 DTO = engagement-metrics-read-service 的 EngagementData（混合就绪度：read_trend
 * live；retention_curves 骨架 live · 留存值 PENDING_INSTRUMENTATION；content_types PARTIAL
 * ——finish/avg 口径待定列 null，adapter 降级 pending 标记，页面灰显禁显假数字）。
 */
export function useMetricsEngagement({
  range = '30d',
  appScope = 'all',
}: UseMetricsEngagementOptions = {}): {
  data: MetricsEngagementViewData;
  isLoading: boolean;
  isError: boolean;
  hasData: boolean;
  refetch: () => void;
} {
  const q = useQuery({
    queryKey: ['metrics', 'engagement', appScope, range],
    queryFn: async (): Promise<MetricsEngagementViewData> => {
      // @real — GET /api/v1/metrics/engagement?app_scope=<all|uuid>&range=<range> → adaptMetricsEngagement
      const dto = await apiRequest<MetricsEngagementResponse>({
        method: 'GET',
        path: '/api/v1/metrics/engagement',
        query: { app_scope: appScope, range },
      });
      return adaptMetricsEngagement(dto);
    },
  });
  // fallback 防解构 undefined，isError + hasData 供页面分流错误态
  return {
    data: q.data ?? METRICS_ENGAGEMENT_FALLBACK,
    isLoading: q.isLoading,
    isError: q.isError,
    hasData: q.data !== undefined,
    refetch: () => {
      void q.refetch();
    },
  };
}

// ---------- 数据中台 · 诊断下钻（ADM-MTR-S006）----------

const METRICS_DIAGNOSIS_FALLBACK: MetricsDiagnosisViewData = {
  computedAt: null,
  window: { range: null, from: '', to: '' },
  dimensions: { channel: 'ready', hour: 'pending', book: 'pending' },
  pending: [],
  rows: [],
};

/** useMetricsDiagnosis 参数：时间窗 + App scope + 渠道多选过滤（CSV 下发，服务端过滤） */
export type UseMetricsDiagnosisOptions = {
  range?: MetricsRange;
  appScope?: string;
  /** 渠道多选（media_source 值；[] = 不过滤） */
  channel?: string[];
};

/**
 * 数据中台 L3 诊断下钻取数（ADM-MTR-S006）。
 *
 * appScope / range / channel 进 queryKey（多选排序后 CSV——同组选择不同点选顺序
 * 命中同一缓存）——切维度重取；服务端 DTO = diagnosis-metrics-read-service 的 DiagnosisData
 * （收入派生 revenue_d7/ltv_d30 按就绪闸控返 PENDING_INSTRUMENTATION，adapter 降级 pending
 * 单元，页面灰显禁显假数字）。hour_bucket/book_id 维度无源
 * （服务端非空即 422）——本 hook 不提供入参通道，UI 侧按禁用态呈现。
 */
export function useMetricsDiagnosis({
  range = '30d',
  appScope = 'all',
  channel = [],
}: UseMetricsDiagnosisOptions = {}): {
  data: MetricsDiagnosisViewData;
  isLoading: boolean;
  isError: boolean;
  hasData: boolean;
  refetch: () => void;
} {
  const channelCsv = [...channel].sort().join(',');
  const q = useQuery({
    queryKey: ['metrics', 'diagnosis', appScope, range, channelCsv],
    queryFn: async (): Promise<MetricsDiagnosisViewData> => {
      // @real — GET /api/v1/metrics/diagnosis?app_scope&range[&channel]（CSV 多选）→ adaptMetricsDiagnosis
      const dto = await apiRequest<MetricsDiagnosisResponse>({
        method: 'GET',
        path: '/api/v1/metrics/diagnosis',
        query: {
          app_scope: appScope,
          range,
          ...(channelCsv ? { channel: channelCsv } : {}),
        },
      });
      return adaptMetricsDiagnosis(dto);
    },
  });
  // 同 useMetricsEngagement：fallback 防解构 undefined，isError + hasData 供页面分流错误态
  return {
    data: q.data ?? METRICS_DIAGNOSIS_FALLBACK,
    isLoading: q.isLoading,
    isError: q.isError,
    hasData: q.data !== undefined,
    refetch: () => {
      void q.refetch();
    },
  };
}

// ---------- 数据中台 · 商业化（ADM-MTR-S004）----------

const METRICS_MONETIZATION_FALLBACK: MetricsMonetizationViewData = {
  revenueReady: false,
  window: { from: '', to: '' },
  kpi: {
    firstChargeRate: null,
    firstChargeDeltaPp: null,
    arppuMinor: null,
    arppuDeltaPct: null,
    repurchaseRate: null,
    repurchaseDeltaPp: null,
  },
  sparks: { firstCharge: [], arppu: [], repurchase: [] },
  trend: { xLabels: [], arppuMinor: [] },
  funnel: { steps: [], overallRate: null },
};

/** useMetricsMonetization 参数：时间窗 + App scope（商业化页顶栏 appId 显式传入） */
export type UseMetricsMonetizationOptions = {
  range?: MetricsRange;
  appScope?: string;
};

/**
 * 数据中台 L2-C 商业化取数（ADM-MTR-S004）。
 *
 * appScope / range 进 queryKey `['metrics', 'monetization', appScope, range]`——切维度重取；
 * 服务端 DTO = monetization-metrics-read-service 的 MonetizationData（混合就绪度：首充/复购/
 * 漏斗交易级次 live；ARPPU revenueReady 闸控；渗透率 + 漏斗行为级次恒 PENDING_INSTRUMENTATION，
 * adapter 降级 pending 标记，页面灰显禁显假数字）。
 */
export function useMetricsMonetization({
  range = '30d',
  appScope = 'all',
}: UseMetricsMonetizationOptions = {}): {
  data: MetricsMonetizationViewData;
  isLoading: boolean;
  isError: boolean;
  hasData: boolean;
  refetch: () => void;
} {
  const q = useQuery({
    queryKey: ['metrics', 'monetization', appScope, range],
    queryFn: async (): Promise<MetricsMonetizationViewData> => {
      // @real — GET /api/v1/metrics/monetization?app_scope=<all|uuid>&range=<range> → adaptMetricsMonetization
      const dto = await apiRequest<MetricsMonetizationResponse>({
        method: 'GET',
        path: '/api/v1/metrics/monetization',
        query: { app_scope: appScope, range },
      });
      return adaptMetricsMonetization(dto);
    },
  });
  // 同 useMetricsEngagement：fallback 防解构 undefined，isError + hasData 供页面分流错误态
  return {
    data: q.data ?? METRICS_MONETIZATION_FALLBACK,
    isLoading: q.isLoading,
    isError: q.isError,
    hasData: q.data !== undefined,
    refetch: () => {
      void q.refetch();
    },
  };
}

// ---------- 数据中台 · LTV 队列（ADM-MTR-S005）----------

const METRICS_LTV_FALLBACK: MetricsLtvCohortsViewData = {
  revenueReady: false,
  computedAt: null,
  rows: [],
};

/** useMetricsLtvCohorts 参数：本页契约是 from/to 日窗 + group_by 维度（非 range 闭集） */
export type UseMetricsLtvCohortsOptions = {
  appScope?: string;
  groupBy?: LtvCohortGroupBy;
  /** YYYY-MM-DD（契约缺省近 90 天；页面显式传，保证 queryKey 确定可重取） */
  from?: string;
  to?: string;
};

/**
 * 数据中台 L2-E LTV 队列取数（ADM-MTR-S005）。
 *
 * appScope / groupBy / from / to 进 queryKey `['metrics', 'ltv-cohorts', ...]`——切维度重取；
 * 服务端 DTO = ltv-metrics-read-service 的 listLtvCohorts 返回值（readiness 契约 dev-docs #392：
 * revenue_ready 缺省/false 或行 revenue PENDING → adapter 落 revenueReady=false，页面热力表
 * 灰显禁显假数字；cohort_size / payers 恒 live）。
 */
export function useMetricsLtvCohorts({
  appScope = 'all',
  groupBy = 'none',
  from,
  to,
}: UseMetricsLtvCohortsOptions): {
  data: MetricsLtvCohortsViewData;
  isLoading: boolean;
  isError: boolean;
  serviceUpdating: boolean;
  hasData: boolean;
  refetch: () => void;
} {
  const q = useQuery({
    queryKey: ['metrics', 'ltv-cohorts', appScope, groupBy, from, to],
    queryFn: async (): Promise<MetricsLtvCohortsViewData> => {
      // @real — GET /api/v2/metrics/ltv-cohorts?app_scope&group_by&from&to → adaptMetricsLtvCohorts
      const dto = await apiRequest<MetricsLtvCohortsResponse>({
        method: 'GET',
        path: '/api/v2/metrics/ltv-cohorts',
        query: { app_scope: appScope, group_by: groupBy, from, to },
      });
      return adaptMetricsLtvCohorts(dto);
    },
  });
  // 同 useMetricsEngagement：fallback 防解构 undefined，isError + hasData 供页面分流错误态
  return {
    data: q.data ?? METRICS_LTV_FALLBACK,
    isLoading: q.isLoading,
    isError: q.isError,
    serviceUpdating: q.error != null && 'httpStatus' in q.error && q.error.httpStatus === 404,
    hasData: q.data !== undefined,
    refetch: () => {
      void q.refetch();
    },
  };
}

// ---------- System config（语言 + 区域）----------
/**
 * sys-config loading / 失败兜底：稳定模块常量（同一引用）——避免每次渲染 `?? {…}`
 * 新建对象触发消费方 useEffect 死循环（codex adversarial #53 r2：error-fallback 被当
 * 成功空集 + 新引用每渲染重跑 setRows → maximum update depth）。配合返回的 isSuccess
 * 让消费方区分「成功返回空集」与「loading/error 兜底」。
 */
const SYS_CONFIG_FALLBACK: { languages: SystemLanguage[]; regions: SystemRegion[] } = {
  languages: [],
  regions: [],
};

export function useSysConfig(): {
  data: { languages: SystemLanguage[]; regions: SystemRegion[] };
  isLoading: boolean;
  /** 查询成功落地（含成功返回空集）；消费方据此区分「服务端真值空」与「兜底空」 */
  isSuccess: boolean;
} {
  const q = useQuery({
    queryKey: ['sys-config'],
    queryFn: async (): Promise<{ languages: SystemLanguage[]; regions: SystemRegion[] }> => {
      // @real — 语言 + 区域两端点并行（GET /system-config/languages + /regions）
      // → sysconfig-adapter 塑成 {languages,regions}（updatedAt 裁分钟、updated_by null→seed）。
      const [languages, regions] = await Promise.all([
        apiRequest<SystemLanguageResponse[]>({
          method: 'GET',
          path: '/api/v1/system-config/languages',
        }),
        apiRequest<SystemRegionResponse[]>({
          method: 'GET',
          path: '/api/v1/system-config/regions',
        }),
      ]);
      const shaped = {
        languages: adaptSystemLanguages(languages ?? []),
        regions: adaptSystemRegions(regions ?? []),
      };
      // 语言中文名 SSOT = system_languages.display_name_i18n.zh —— 注入 format 的运行时表，
      // 让 langName / fmtLang（纯函数，adapter / 非 React 上下文也在调）全站统一走 DB 真值。
      //
      // **必须在 queryFn 内注册，不能放回 useEffect**：effect 跑在 commit 之后，而数据到达只触发
      // 一轮渲染（pending→success 同批）——那一轮的 JSX 里 langName 读的仍是兜底表，且此后没有
      // 任何渲染来纠正它。即便是**直接订阅 useSysConfig 的页面**也吃这一帧陈旧值（今天看不出来，
      // 只因随后总会因别的 query 落地 / 交互再渲染一次，那是巧合不是保证）。放这里则注册严格早于
      // 「写缓存 → 通知 observer → 重渲染」，观察者拿到 data 的那一帧读到的已是 DB 真值。
      // 每次 fetch 恰好跑一次，不受 StrictMode 的 effect 双跑影响；整表替换、幂等。
      registerLangNames(
        Object.fromEntries(shaped.languages.map((l) => [l.language_code, l.display_name_i18n?.zh])),
      );
      return shaped;
    },
  });
  return { data: q.data ?? SYS_CONFIG_FALLBACK, isLoading: q.isLoading, isSuccess: q.isSuccess };
}

// ---------- Admin 团队成员 / 操作日志（settings 域）----------
// 稳定 fallback：每渲染复用同一引用，避免 `?? []` 每渲染新建空数组触发 effect 死循环（checklist #1）
const ADMIN_USERS_FALLBACK: AdminAccountResponse[] = [];
const AUDIT_LOGS_FALLBACK: AdminAuditLogResponse[] = [];

/**
 * 团队成员列表（GET /api/v1/admin-users，后端 requireRole('root')）。
 * enabled=false（非 root 会话）时无视缓存返回 fallback 空集（checklist #5 防降权读残留）。
 */
export function useAdminUsers(enabled = true): {
  data: AdminAccountResponse[];
  isLoading: boolean;
} {
  const q = useQuery({
    queryKey: ['admin-users'],
    // @real — adminUsersApi.listOffsetPaged(page)（GET /api/v1/admin-users）；DTO 直消费，无需 adapter
    // GET /admin-users 改 offset 分页（okOffsetPaged，默认/上限 limit=200）。团队成员是受限小集合（运营账号），
    // 本 hook 按 page 递增累积全量供成员表整表渲染（不另设分页器 UI）；maxPages 上限兜底防 server 异常死循环。
    queryFn: async (): Promise<AdminAccountResponse[]> => {
      const rows: AdminAccountResponse[] = [];
      const maxPages = 50;
      for (let i = 1; i <= maxPages; i++) {
        const page = await adminUsersApi.listOffsetPaged(i, 200);
        rows.push(...page.data);
        if (!page.hasMore || page.data.length === 0) break;
      }
      return rows;
    },
    enabled,
  });
  return {
    data: enabled ? (q.data ?? ADMIN_USERS_FALLBACK) : ADMIN_USERS_FALLBACK,
    isLoading: q.isLoading,
  };
}

const PENDING_INVITES_FALLBACK: PendingInviteResponse[] = [];

/**
 * 待接受邀请（GET /api/v1/auth/invites，root/admin）——surface admin_invites。
 * 团队成员列表只含已激活 admin_users 行；邀请中的成员在此（接受后才迁入成员列表）。
 * enabled=false（非 root/admin 会话）→ 返空集不发请求。
 */
export function usePendingInvites(enabled = true): {
  data: PendingInviteResponse[];
  isLoading: boolean;
} {
  const q = useQuery({
    queryKey: ['pending-invites'],
    // @real — listPendingInvites()（GET /api/v1/auth/invites）；裸数组直透
    queryFn: async (): Promise<PendingInviteResponse[]> => (await listPendingInvites()) ?? [],
    enabled,
  });
  return {
    data: enabled ? (q.data ?? PENDING_INVITES_FALLBACK) : PENDING_INVITES_FALLBACK,
    isLoading: q.isLoading,
  };
}

/** 操作日志服务端下推筛选维度（与 GET /api/v2/audit-logs 契约一致；分页 cursor/limit 不进此对象）。 */
export type AuditLogsFilters = {
  /** action OR resource 模糊搜索（单值） */
  q?: string;
  /** 动作多选（重复 query 参数 → string[]） */
  action?: string[];
  /** created_at 时间窗起（ISO 8601 带时区偏移；from ≤ created_at） */
  from?: string;
  /** created_at 时间窗止（ISO 8601 带时区偏移；created_at < to） */
  to?: string;
  /** 操作者多选（root 专用维度；非 root 被后端 self 覆盖）。服务端 inArray 下推，不再单页客户端再筛 */
  actor_id?: string[];
  /** 资源类型精确匹配 */
  resource?: string;
  /** 操作对象精确匹配（UUID） */
  resource_id?: string;
};

/** 把审计筛选对象塑成稳定 queryKey 片段（同 bookFilterKey 口径，数组排序保证顺序无关）。 */
function auditFilterKey(filters?: AuditLogsFilters): string {
  if (!filters) return '';
  return JSON.stringify({
    q: filters.q ?? '',
    action: [...(filters.action ?? [])].sort(),
    from: filters.from ?? '',
    to: filters.to ?? '',
    actor_id: [...(filters.actor_id ?? [])].sort(),
    resource: filters.resource ?? '',
    resource_id: filters.resource_id ?? '',
  });
}

/**
 * 操作日志（GET /api/v2/audit-logs；任意已登录角色可看，非 root 仅自己）。
 * offset 分页（admin 列表默认 offset，dev-docs 40-分页与排序）+ 服务端下推筛选；旧 useAuditLogs 保留给只读首页消费。
 * 注意：filter 变化时页面应把 page 重置回第一页（filterKey 进 queryKey 仅保证 filter 变化触发重取）。
 * page/limit 经后端 Zod 校验（非法 → 422），这里传整数。
 */
export function useAuditLogsPage(params: {
  page: number;
  limit: number;
  filters?: AuditLogsFilters;
}): OffsetPageView<AdminAuditLogResponse> & {
  isFetching: boolean;
  isServiceUpdating: boolean;
  refetch: () => void;
} {
  const q = useQuery({
    queryKey: [
      'audit-logs',
      'v2',
      'page',
      params.page,
      params.limit,
      auditFilterKey(params.filters),
    ],
    retry: false,
    // @real — auditLogsApi.listOffsetPaged({ page, limit, ...filters })（GET /api/v2/audit-logs?page&limit&<filters>）
    queryFn: () =>
      auditLogsApi.listOffsetPaged({
        page: params.page,
        limit: params.limit,
        ...(params.filters?.q ? { q: params.filters.q } : {}),
        ...(params.filters?.action && params.filters.action.length > 0
          ? { action: params.filters.action }
          : {}),
        ...(params.filters?.from ? { from: params.filters.from } : {}),
        ...(params.filters?.to ? { to: params.filters.to } : {}),
        ...(params.filters?.actor_id && params.filters.actor_id.length > 0
          ? { actor_id: params.filters.actor_id }
          : {}),
        ...(params.filters?.resource ? { resource: params.filters.resource } : {}),
        ...(params.filters?.resource_id ? { resource_id: params.filters.resource_id } : {}),
      }),
  });
  return {
    data: q.data?.data ?? AUDIT_LOGS_FALLBACK,
    isLoading: q.isLoading,
    isError: q.isError,
    isFetching: q.isFetching,
    isServiceUpdating: q.error instanceof AdminApiError && q.error.httpStatus === 404,
    refetch: () => {
      void q.refetch();
    },
    total: q.data?.total ?? 0,
    hasMore: q.data?.hasMore ?? false,
  };
}

export function useAuditLogs(limit = 100): {
  data: AdminAuditLogResponse[];
  isLoading: boolean;
  isError: boolean;
  isServiceUpdating: boolean;
} {
  const q = useQuery({
    queryKey: ['audit-logs', 'v2', limit],
    retry: false,
    // @real — auditLogsApi.list({ limit })（GET /api/v2/audit-logs）；DTO 直消费
    queryFn: () => auditLogsApi.list({ limit }),
  });
  return {
    data: q.data ?? AUDIT_LOGS_FALLBACK,
    isLoading: q.isLoading,
    isError: q.isError,
    isServiceUpdating: q.error instanceof AdminApiError && q.error.httpStatus === 404,
  };
}

const AUDIT_FACETS_FALLBACK: AuditLogFacetsResponse = { actions: [], actors: [] };
/** 操作日志筛选 facet（全量动作 + 操作员，按角色收敛）：供筛选下拉取全量选项（替代当前页去重）。 */
export function useAuditLogFacets(): {
  data: AuditLogFacetsResponse;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  isServiceUpdating: boolean;
  refetch: () => void;
} {
  const q = useQuery({
    queryKey: ['audit-logs', 'v2', 'facets'],
    retry: false,
    // @real — auditLogsApi.facets()（GET /api/v2/audit-logs/facets）
    queryFn: () => auditLogsApi.facets(),
  });
  return {
    data: q.data ?? AUDIT_FACETS_FALLBACK,
    isLoading: q.isLoading,
    isFetching: q.isFetching,
    isError: q.isError,
    isServiceUpdating: q.error instanceof AdminApiError && q.error.httpStatus === 404,
    refetch: () => {
      void q.refetch();
    },
  };
}

// ---------- Me · 登录会话 / 可信设备（profile 域，本人账户操作）----------
// loading / 失败兜底：稳定模块常量（同一引用）——避免每渲染 `?? []` 新建空数组，
// 触发消费方 useEffect 依赖每渲染变化（同 RISK_RULES_FALLBACK / SYS_CONFIG_FALLBACK）。
const ME_SESSIONS_FALLBACK: SelfSessionResponse[] = [];
const ME_TRUSTED_DEVICES_FALLBACK: TrustedDeviceResponse[] = [];

/**
 * 当前 admin 登录会话列表（GET /api/v1/me/sessions）。
 * DTO 不含 token/hash，仅元信息（ua/ip/remember/时间）供辨认并撤销。响应不带 current 标记
 * （access token 不携带 session_id，无法反推当前会话；契约已注明，本轮不做 current 推断）。
 */
export function useMeSessions(): { data: SelfSessionResponse[]; isLoading: boolean } {
  // query key 带当前 admin userId：同浏览器换账号时缓存不串（用户 B 不读用户 A 的会话）。
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const q = useQuery({
    queryKey: ['me-sessions', userId],
    queryFn: async (): Promise<SelfSessionResponse[]> => {
      // @real — meApi.listSessions()（GET /api/v1/me/sessions）；裸数组直透（页面自渲染）
      const rows = await meApi.listSessions();
      return rows ?? [];
    },
  });
  return { data: q.data ?? ME_SESSIONS_FALLBACK, isLoading: q.isLoading };
}

/**
 * 当前 admin 可信设备列表（GET /api/v1/me/trusted-devices，INFRA-AUTH-007）。
 * 「记住此设备 30 天」勾选后签发的 device_token 对应设备；DTO 不含 token/hash，仅元信息供辨认并撤销。
 */
export function useMeTrustedDevices(): { data: TrustedDeviceResponse[]; isLoading: boolean } {
  // query key 带当前 admin userId：同浏览器换账号时缓存不串（用户 B 不读用户 A 的可信设备）。
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const q = useQuery({
    queryKey: ['me-trusted-devices', userId],
    queryFn: async (): Promise<TrustedDeviceResponse[]> => {
      // @real — meApi.listTrustedDevices()（GET /api/v1/me/trusted-devices）；裸数组直透
      const rows = await meApi.listTrustedDevices();
      return rows ?? [];
    },
  });
  return { data: q.data ?? ME_TRUSTED_DEVICES_FALLBACK, isLoading: q.isLoading };
}

/** 未建行时的默认通知偏好投影（空 prefs + 勿扰关闭）；与后端 toResponse 默认一致 */
const ME_NOTIFICATION_PREFS_FALLBACK: NotificationPrefsResponse = {
  prefs: {},
  dnd_enabled: false,
  dnd_start: null,
  dnd_end: null,
};

/**
 * 当前 admin 通知偏好 + 勿扰时段（GET /api/v1/me/notification-preferences）。
 * 通知类型 taxonomy + 默认值由前端拥有（ProfilePage Notify ROWS）；本 query 返回已持久化的覆盖值。
 */
export function useMeNotificationPrefs(): {
  data: NotificationPrefsResponse;
  isLoading: boolean;
} {
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const q = useQuery({
    queryKey: ['me-notification-prefs', userId],
    queryFn: async (): Promise<NotificationPrefsResponse> => {
      // @real — meApi.getNotificationPrefs()（GET /api/v1/me/notification-preferences）
      return await meApi.getNotificationPrefs();
    },
  });
  return { data: q.data ?? ME_NOTIFICATION_PREFS_FALLBACK, isLoading: q.isLoading };
}

/** OpenRouter 全局配置只读视图；真实端点，失败不会伪造已接入状态。 */
export function useOpenRouterCredential() {
  return useQuery<OpenRouterCredentialResponse>({
    queryKey: ['integrations', 'openrouter'],
    queryFn: () => openrouterApi.getCredential(),
  });
}

/** 百炼全局配置只读视图；凭据与 OpenRouter 分开管理。 */
export function useBailianCredential() {
  return useQuery<BailianCredentialResponse>({
    queryKey: ['integrations', 'bailian'],
    queryFn: () => bailianApi.getCredential(),
  });
}

/** Ollama 全局 Tunnel 配置；只缓存掩码视图，不含 Access Secret。 */
export function useOllamaCredential() {
  return useQuery<OllamaCredentialResponse>({
    queryKey: ['integrations', 'ollama'],
    queryFn: () => ollamaApi.getCredential(),
  });
}

/** Hugging Face 全局配置；只缓存掩码视图，不含 Token。 */
export function useHuggingFaceCredential() {
  return useQuery<HuggingFaceCredentialResponse>({
    queryKey: ['integrations', 'huggingface'],
    queryFn: () => huggingFaceApi.getCredential(),
  });
}

/** 运营违禁词库列表，真实接口，筛选和分页纳入缓存键。 */
export function useProhibitedTerms(query: Parameters<typeof prohibitedTermsApi.list>[0]) {
  return useQuery({
    queryKey: ['prohibited-terms', query],
    queryFn: () => prohibitedTermsApi.list(query),
  });
}

/** 各模型服务商的执行并发槽数（已合并默认值）。 */
export function useLandingAiProviderConcurrency() {
  return useQuery({
    queryKey: ['model-configurations', 'provider-concurrency'],
    queryFn: () => landingAiProviderConcurrencyApi.get(),
  });
}

export function useLandingAiConfig(
  enabled = true,
  scene: 'desensitization' | 'review' = 'desensitization',
) {
  return useQuery({
    queryKey: ['model-configurations', `landing-html-${scene}`],
    queryFn: () => landingAiConfigApi.get(scene),
    enabled,
  });
}

export function useLandingAiReviews(id: string, taskVersion?: string) {
  const queryClient = useQueryClient();
  const query = useQuery({
    // 任务版本变化必须重取记录，避免沿用修改前的有效结论。
    queryKey: ['landing-ai-reviews', id, taskVersion],
    queryFn: () => landingAiReviewsApi.list(id),
    refetchInterval: (query) =>
      query.state.data?.some(
        (row) => (row.status === 'PENDING' || row.status === 'RUNNING') && !row.can_retry,
      )
        ? 3000
        : false,
  });
  const latestId = query.data?.[0]?.id;
  const latestStatus = query.data?.[0]?.status;
  useEffect(() => {
    if (!latestId || !['SUCCEEDED', 'FAILED'].includes(latestStatus ?? '')) return;
    // 后台规则处理即使 AI 失败也可能已切换 HTML；刷新下载版本和独立规则日志。
    void queryClient.invalidateQueries({ queryKey: ['landing-pages'] });
    void queryClient.invalidateQueries({ queryKey: ['landing-desensitization-logs', id] });
  }, [id, latestId, latestStatus, queryClient]);
  return query;
}

export function useLandingDesensitizationRuns(id: string) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['landing-desensitization-runs', id],
    queryFn: () => landingDesensitizationLogsApi.runs(id),
    refetchInterval: (query) =>
      query.state.data?.some((run) => ['PENDING', 'RUNNING'].includes(run.status) && !run.can_retry)
        ? 3000
        : false,
  });
  const latestId = query.data?.[0]?.id;
  const latestStatus = query.data?.[0]?.status;
  useEffect(() => {
    if (!latestId || !['SUCCEEDED', 'FAILED'].includes(latestStatus ?? '')) return;
    void qc.invalidateQueries({ queryKey: ['landing-pages'] });
    void qc.invalidateQueries({ queryKey: ['landing-desensitization-logs', id] });
    void qc.invalidateQueries({ queryKey: ['landing-ai-reviews', id] });
  }, [id, latestId, latestStatus, qc]);
  return query;
}

export function useLandingDesensitizationLogs(id: string, taskVersion?: string) {
  return useQuery({
    queryKey: ['landing-desensitization-logs', id, taskVersion],
    queryFn: () => landingDesensitizationLogsApi.list(id),
  });
}
export function useLandingDesensitizationDetail(id: string, logId: string | null) {
  return useQuery({
    queryKey: ['landing-desensitization-detail', id, logId],
    queryFn: () => landingDesensitizationLogsApi.detail(id, logId ?? ''),
    enabled: logId !== null,
  });
}

// 投放项目沿用集中 Query 边界；页面不直接调用原始网络请求。
export * from './landing-project-queries';
// 服务器监控（root/admin 只读）同样经集中 Query 边界导出。
export * from './server-monitor-queries';
export type {
  ProjectSummary,
  ProjectDetail,
  ProjectChapter,
  ProjectAsset,
  ProjectTask,
  ProjectProgress,
  ProjectQueueExecution,
  ProjectQueueState,
  ProjectOperation,
  ProjectMutation,
  ProjectChange,
  ProjectHistory,
} from '~/hooks/use-landing-projects';

/** Web 投放链接读写；失效计划详情与链接列表，页面只消费真实 API。 */
export function useAdPlanWebLinks(id: string) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['ad-plans'] });
  const query = useQuery({
    queryKey: ['ad-plans', 'web-links', id],
    queryFn: () => adPlansApi.webLinks(id),
  });
  const create = useMutation({
    mutationFn: (body: Parameters<typeof adPlansApi.createWebLink>[1]) =>
      adPlansApi.createWebLink(id, body),
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: ({
      linkId,
      body,
    }: { linkId: string; body: Parameters<typeof adPlansApi.updateWebLink>[2] }) =>
      adPlansApi.updateWebLink(id, linkId, body),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (linkId: string) => adPlansApi.removeWebLink(id, linkId),
    onSuccess: invalidate,
  });
  return { query, create, update, remove };
}

export { useUserPresetTagAssignment, useUserPresetTagDefinitions } from './user-preset-tag-queries';

// ---------- 数据中台 · 付费转化看板（ADM-MTR-S012）----------

/** 付费转化看板查询参数（页面筛选态 → GET /api/v1/metrics/paywall-conversion 查询串） */
export type UseMetricsPaywallConversionOptions = {
  from: string;
  to: string;
  groupBy: PaywallConversionDimension[];
  appIds: string[];
  languages: string[];
  channels: string[];
  platforms: string[];
  appVersions: string[];
  scenarios: string[];
  paymentChannels: string[];
  userType: '' | 'new' | 'returning';
  payerStatus: '' | 'payer' | 'non_payer';
  minActiveUsers: number | null;
  /** false 时不发请求（如时间窗超出接口上限） */
  enabled?: boolean;
};

/** useMetricsPaywallConversion 的单次取数（逐级嵌套分组按前缀复用同一条取数） */
export async function fetchMetricsPaywallConversion(
  options: UseMetricsPaywallConversionOptions,
): Promise<PaywallConversionViewData> {
  // @real — GET /api/v1/metrics/paywall-conversion → adaptMetricsPaywallConversion
  const query: Record<string, string | string[]> = {
    from: options.from,
    to: options.to,
    group_by: options.groupBy.join(','),
  };
  const lists: Array<[string, string[]]> = [
    ['app_ids', options.appIds],
    ['languages', options.languages],
    ['channels', options.channels],
    ['platforms', options.platforms],
    ['app_versions', options.appVersions],
    ['scenarios', options.scenarios],
    ['payment_channels', options.paymentChannels],
  ];
  // 多值走重复参数：渠道 / 版本等自由文本可能自带逗号，不能按逗号拼接
  for (const [key, values] of lists) if (values.length) query[key] = values;
  if (options.userType) query.user_type = options.userType;
  if (options.payerStatus) query.payer_status = options.payerStatus;
  if (options.minActiveUsers !== null) query.min_active_users = String(options.minActiveUsers);
  const dto = await apiRequest<PaywallConversionData>({
    method: 'GET',
    path: '/api/v1/metrics/paywall-conversion',
    query,
  });
  // 按支付方式分组时，所选通道（未筛选 = 结算通道闭集全部）无数据也出 0 行，不隐藏
  return withZeroPaymentChannelRows(
    adaptMetricsPaywallConversion(dto),
    options.paymentChannels.length > 0 ? options.paymentChannels : SETTLEMENT_CHANNELS,
  );
}

export const metricsPaywallConversionQueryKey = (options: UseMetricsPaywallConversionOptions) =>
  ['metrics', 'paywall-conversion', { ...options, enabled: undefined }] as const;

/**
 * 付费转化看板取数：分组表格 / 时间趋势 / 转化链路共用。全部筛选进 queryKey，切换即重取；
 * 列表参数以逗号拼接下发（服务端去重并与书写顺序无关地缓存）。
 */
export function useMetricsPaywallConversion(options: UseMetricsPaywallConversionOptions): {
  data: PaywallConversionViewData | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const q = useQuery({
    queryKey: metricsPaywallConversionQueryKey(options),
    enabled: options.enabled ?? true,
    placeholderData: keepPreviousData,
    queryFn: () => fetchMetricsPaywallConversion(options),
  });
  return {
    data: q.data,
    isLoading: q.isLoading,
    isFetching: q.isFetching,
    isError: q.isError,
    refetch: () => {
      void q.refetch();
    },
  };
}

// ---------- 数据中台 · 激活转化看板（ADM-MTR-S013）----------

/** 激活转化看板查询参数（页面筛选态 → GET /api/v1/metrics/activation-conversion 查询串） */
export type UseMetricsActivationConversionOptions = {
  from: string;
  to: string;
  groupBy: ActivationConversionDimension[];
  hours: number[];
  appIds: string[];
  channelTypes: string[];
  appVersions: string[];
  countries: string[];
  adLanguages: string[];
  bookCodes: string[];
  novelIds: string[];
  minActivations: number | null;
  /** false 时不发请求（如时间窗超出接口上限） */
  enabled?: boolean;
};

/** useMetricsActivationConversion 的单次取数（逐级嵌套分组按前缀复用同一条取数） */
export async function fetchMetricsActivationConversion(
  options: UseMetricsActivationConversionOptions,
): Promise<ActivationConversionViewData> {
  // @real — GET /api/v1/metrics/activation-conversion → adaptMetricsActivationConversion
  const query: Record<string, string | string[]> = {
    from: options.from,
    to: options.to,
    group_by: options.groupBy.join(','),
  };
  const lists: Array<[string, string[]]> = [
    ['hours', options.hours.map(String)],
    ['app_ids', options.appIds],
    ['channel_types', options.channelTypes],
    ['app_versions', options.appVersions],
    ['countries', options.countries],
    ['ad_languages', options.adLanguages],
    ['book_codes', options.bookCodes],
    ['novel_ids', options.novelIds],
  ];
  for (const [key, values] of lists) if (values.length) query[key] = values;
  if (options.minActivations !== null) query.min_activations = String(options.minActivations);
  const dto = await apiRequest<ActivationConversionData>({
    method: 'GET',
    path: '/api/v1/metrics/activation-conversion',
    query,
  });
  return adaptMetricsActivationConversion(dto);
}

export const metricsActivationConversionQueryKey = (
  options: UseMetricsActivationConversionOptions,
) => ['metrics', 'activation-conversion', { ...options, enabled: undefined }] as const;

/**
 * 激活转化看板取数：分组表格 / 时间趋势 / 转化链路共用。全部筛选进 queryKey，切换即重取；
 * 多值参数走重复参数（书籍编码 / 版本等自由文本可能自带逗号）。
 */
export function useMetricsActivationConversion(options: UseMetricsActivationConversionOptions): {
  data: ActivationConversionViewData | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const q = useQuery({
    queryKey: metricsActivationConversionQueryKey(options),
    enabled: options.enabled ?? true,
    placeholderData: keepPreviousData,
    queryFn: () => fetchMetricsActivationConversion(options),
  });
  return {
    data: q.data,
    isLoading: q.isLoading,
    isFetching: q.isFetching,
    isError: q.isError,
    refetch: () => {
      void q.refetch();
    },
  };
}

// ---------- 数据中台 · Web 行为漏斗看板（ADM-MTR-S016）----------

/** Web 行为漏斗查询参数（页面筛选态 → GET /api/v1/metrics/web-funnels 查询串） */
export type UseMetricsWebFunnelsOptions = {
  appId: string;
  analysis: WebFunnelAnalysis;
  from: string;
  to: string;
  groupBy: WebFunnelDimension[];
  languages: string[];
  countries: string[];
  sources: string[];
  novelIds: string[];
  adLinkIds: string[];
  chapterIndex: number | null;
  maxChapterIndex: number | null;
  /** false 时不发请求（未选 Web App、时间窗越界） */
  enabled?: boolean;
};

/**
 * Web 行为漏斗取数：单个 WEB App × 单个分析。全部筛选进 queryKey，切换即重取；多值参数走重复参数。
 * 页面只传当前分析可用的筛选，避免服务端按分析闭集拒绝。
 */
export function useMetricsWebFunnels(options: UseMetricsWebFunnelsOptions): {
  data: WebFunnelViewData | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const q = useQuery({
    queryKey: ['metrics', 'web-funnels', { ...options, enabled: undefined }],
    enabled: options.enabled ?? true,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<WebFunnelViewData> => {
      // @real — GET /api/v1/metrics/web-funnels → adaptMetricsWebFunnels
      const query: Record<string, string | string[]> = {
        app_id: options.appId,
        analysis: options.analysis,
        from: options.from,
        to: options.to,
      };
      if (options.groupBy.length) query.group_by = options.groupBy.join(',');
      const lists: Array<[string, string[]]> = [
        ['languages', options.languages],
        ['countries', options.countries],
        ['sources', options.sources],
        ['novel_ids', options.novelIds],
        ['ad_link_ids', options.adLinkIds],
      ];
      for (const [key, values] of lists) if (values.length) query[key] = values;
      if (options.chapterIndex !== null) query.chapter_index = String(options.chapterIndex);
      if (options.maxChapterIndex !== null)
        query.max_chapter_index = String(options.maxChapterIndex);
      const dto = await apiRequest<WebFunnelData>({
        method: 'GET',
        path: '/api/v1/metrics/web-funnels',
        query,
      });
      return adaptMetricsWebFunnels(dto);
    },
  });
  return {
    data: q.data,
    isLoading: q.isLoading,
    isFetching: q.isFetching,
    isError: q.isError,
    refetch: () => {
      void q.refetch();
    },
  };
}

// ---------- 数据中台 · 付费 cohort（ADM-MTR-S014）----------

/** 付费 cohort 查询参数（页面筛选态 → GET /api/v1/metrics/first-payment-cohorts 查询串） */
export type UseMetricsFirstPaymentCohortsOptions = {
  from: string;
  to: string;
  groupBy: FirstPaymentCohortDimension[];
  appIds: string[];
  channelTypes: string[];
  languages: string[];
  minActivations: number | null;
  /** false 时不发请求（如时间窗超出接口上限） */
  enabled?: boolean;
};

/** useMetricsFirstPaymentCohorts 的单次取数（逐级嵌套分组按前缀复用同一条取数） */
export async function fetchMetricsFirstPaymentCohorts(
  options: UseMetricsFirstPaymentCohortsOptions,
): Promise<FirstPaymentCohortViewData> {
  // @real — GET /api/v1/metrics/first-payment-cohorts → adaptMetricsFirstPaymentCohorts
  const query: Record<string, string | string[]> = {
    from: options.from,
    to: options.to,
    group_by: options.groupBy.join(','),
  };
  const lists: Array<[string, string[]]> = [
    ['app_ids', options.appIds],
    ['channel_types', options.channelTypes],
    ['languages', options.languages],
  ];
  for (const [key, values] of lists) if (values.length) query[key] = values;
  if (options.minActivations !== null) query.min_activations = String(options.minActivations);
  const dto = await apiRequest<FirstPaymentCohortData>({
    method: 'GET',
    path: '/api/v1/metrics/first-payment-cohorts',
    query,
  });
  return adaptMetricsFirstPaymentCohorts(dto);
}

export const metricsFirstPaymentCohortsQueryKey = (options: UseMetricsFirstPaymentCohortsOptions) =>
  ['metrics', 'first-payment-cohorts', { ...options, enabled: undefined }] as const;

/** 付费 cohort 取数：全部筛选进 queryKey，切换即重取；多值参数走重复参数 */
export function useMetricsFirstPaymentCohorts(options: UseMetricsFirstPaymentCohortsOptions): {
  data: FirstPaymentCohortViewData | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  /** true = data 是上一组查询参数的占位结果（keepPreviousData），不能当作当前结果展示 */
  isPlaceholderData: boolean;
  refetch: () => void;
} {
  const q = useQuery({
    queryKey: metricsFirstPaymentCohortsQueryKey(options),
    enabled: options.enabled ?? true,
    placeholderData: keepPreviousData,
    queryFn: () => fetchMetricsFirstPaymentCohorts(options),
  });
  return {
    data: q.data,
    isLoading: q.isLoading,
    isFetching: q.isFetching,
    isError: q.isError,
    isPlaceholderData: q.isPlaceholderData,
    refetch: () => {
      void q.refetch();
    },
  };
}

// ---------- 数据中台 · 用户留存（ADM-MTR-S015）----------

/** 用户留存查询参数（页面筛选态 → GET /api/v1/metrics/retention-cohorts 查询串） */
export type UseMetricsRetentionCohortsOptions = {
  granularity: RetentionCohortGranularity;
  from: string;
  to: string;
  userType: RetentionCohortUserType;
  appIds: string[];
  languages: string[];
  countries: string[];
  groupBy: import('@novel-admin/shared').RetentionCohortGroupDimension[];
  /** false 时不发请求（如自选时间窗超出接口上限） */
  enabled?: boolean;
};

/** useMetricsRetentionCohorts 的单次取数（逐级嵌套分组按前缀复用同一条取数） */
export async function fetchMetricsRetentionCohorts(
  options: UseMetricsRetentionCohortsOptions,
): Promise<RetentionCohortViewData> {
  // @real — GET /api/v1/metrics/retention-cohorts → adaptMetricsRetentionCohorts
  const query: Record<string, string | string[]> = {
    granularity: options.granularity,
    from: options.from,
    to: options.to,
    user_type: options.userType,
    group_by: options.groupBy.join(','),
  };
  const lists: Array<[string, string[]]> = [
    ['app_ids', options.appIds],
    ['languages', options.languages],
    ['countries', options.countries],
  ];
  for (const [key, values] of lists) if (values.length) query[key] = values;
  const dto = await apiRequest<RetentionCohortData>({
    method: 'GET',
    path: '/api/v1/metrics/retention-cohorts',
    query,
  });
  return adaptMetricsRetentionCohorts(dto);
}

export const metricsRetentionCohortsQueryKey = (options: UseMetricsRetentionCohortsOptions) =>
  ['metrics', 'retention-cohorts', { ...options, enabled: undefined }] as const;

/** 用户留存取数：全部筛选进 queryKey，切换即重取；多值参数走重复参数 */
export function useMetricsRetentionCohorts(options: UseMetricsRetentionCohortsOptions): {
  data: RetentionCohortViewData | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const q = useQuery({
    queryKey: metricsRetentionCohortsQueryKey(options),
    enabled: options.enabled ?? true,
    placeholderData: keepPreviousData,
    queryFn: () => fetchMetricsRetentionCohorts(options),
  });
  return {
    data: q.data,
    isLoading: q.isLoading,
    isFetching: q.isFetching,
    isError: q.isError,
    refetch: () => {
      void q.refetch();
    },
  };
}

// ---------- 激励广告（ADM-IAA-002/003） ----------
export { rewardedAdsApi } from '~/hooks/use-rewarded-ads';
export type {
  RewardedAdConfig,
  RewardedAdLanguageConfigResponse,
  RewardedAdUnitResponse,
  RewardedAdPlacement,
} from '@novel-admin/shared';
export const rewardedAdQueryKeys = {
  languages: (appId: string) => ['apps', appId, 'rewarded-ad-languages'] as const,
  units: (appId: string) => ['apps', appId, 'rewarded-ad-units'] as const,
};

export function useRewardedAdLanguageConfigs(appId: string) {
  return useQuery({
    queryKey: rewardedAdQueryKeys.languages(appId),
    // @real — AB §8.1 全量语言独立配置
    queryFn: () => rewardedAdsApi.listLanguages(appId),
  });
}

export function useRewardedAdUnits(appId: string, enabled = true) {
  return useQuery({
    queryKey: rewardedAdQueryKeys.units(appId),
    enabled,
    // @real — AB §8.4 仅当前 active 版本
    queryFn: () => rewardedAdsApi.listUnits(appId),
  });
}

/** 推送分析仅投影真实响应；undefined 保留请求失败与真实零活动的区别。 */
export const pushAnalyticsQueryKeys = { all: ['push-analytics'] as const };
export function usePushAnalytics(query: import('@novel-admin/shared').PushAnalyticsQuery) {
  return useQuery({
    queryKey: ['push-analytics', 'overview', query],
    queryFn: () => pushAnalyticsApi.overview(query),
  }); // @real
}
export function usePushAnalyticsNotifications(
  query: import('@novel-admin/shared').PushAnalyticsListQuery,
) {
  return useQuery({
    queryKey: ['push-analytics', 'notifications', query],
    queryFn: () => pushAnalyticsApi.notifications(query),
  }); // @real
}
export function usePushAnalyticsNotification(id: string, appId?: string) {
  return useQuery({
    queryKey: ['push-analytics', 'detail', id, appId],
    queryFn: () => pushAnalyticsApi.detail(id, appId),
  }); // @real
}
export type {
  PushAnalyticsQuery,
  PushAnalyticsResponse,
  PushAnalyticsNotification,
  PushAnalyticsNotificationDetail,
  PushAnalyticsMetricCounts,
} from '@novel-admin/shared';
